import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { RegulatorChargerLayersOverlay } from '../../components/regulator/RegulatorChargerLayersOverlay';
import RegulatorDataLayersDrawer from '../../components/regulator/RegulatorDataLayersDrawer';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';
import { useAuth } from '../../components/router/authContext';
import { hasProductAccess } from '../../utils/productAccess';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { API_ENDPOINTS as LAYER_API } from '../../config/chargerApiConfig';
import CircuitUpdateBanner from '../../components/common/CircuitUpdateBanner';
import AdditionalMatrices from '../../components/regulator/AdditionalMatrices';
import { ABankInfoTip } from '../../components/common/CapacityOutlookTables';
import { FaUpload, FaDownload, FaMapMarkerAlt, FaInfoCircle, FaTrash, FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import { FiLayers } from 'react-icons/fi';
import {
  FiZap,
  FiPackage,
  FiShare2,
  FiTool,
  FiMapPin,
  FiTrendingUp,
  FiGrid,
  FiAlertTriangle,
  FiMap,
} from 'react-icons/fi';
import { PiTrafficSignal } from 'react-icons/pi';
import { initializeGeocoder, reverseGeocode } from '../../utils/geocodingUtil';
import { outlookIndices, circuitABankDisplay, circuitAlcDisplay, formatOutlookEntriesLine } from '../../utils/capacityOutlook';
import { getAssessmentMeta, formatBindingConstraint } from '../../utils/capacityAssessment';

const defaultCenter = { lat: 34.0522, lng: -118.2437 };
const mapContainerStyle = { width: '100%', height: '100%' };
const MAP_FETCH_PAGE_SIZE = 200;
const REGULATOR_MAP_LAYER_PREFS_KEY = 'regulator_map_layer_prefs';
const AUTO_CLICK_PRIORITY_LAYER_ID = 'auto';
const DEFAULT_REGULATOR_MAP_LAYER_PREFS = {
  existingChargersVisible: false,
  warehousesVisible: false,
  intermodalTerminalsVisible: false,
  serviceCentersVisible: false,
  truckHotspotsVisible: false,
  trafficVolumeVisible: false,
  powerGridCapacityVisible: false,
  powerGridHeatmapVisible: false,
  substationsVisible: false,
  utilitiesVisible: false,
  sraVisible: false,
  countiesVisible: false,
  overburdenedVisible: false,
  calEnviroScreenVisible: false,
  calEnviroScreenMinPercentile: 75,
  interactiveTopLayerId: AUTO_CLICK_PRIORITY_LAYER_ID,
};

const clampPercentile = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_REGULATOR_MAP_LAYER_PREFS.calEnviroScreenMinPercentile;
  return Math.max(0, Math.min(100, n));
};

const normalizeRegulatorMapLayerPrefs = (parsed) => {
  if (!parsed || typeof parsed !== 'object') return DEFAULT_REGULATOR_MAP_LAYER_PREFS;
  const interactiveTopLayerId =
    typeof parsed.interactiveTopLayerId === 'string' && parsed.interactiveTopLayerId.trim()
      ? parsed.interactiveTopLayerId
      : AUTO_CLICK_PRIORITY_LAYER_ID;
  return {
    existingChargersVisible: Boolean(parsed.existingChargersVisible),
    warehousesVisible: Boolean(parsed.warehousesVisible),
    intermodalTerminalsVisible: Boolean(parsed.intermodalTerminalsVisible),
    serviceCentersVisible: Boolean(parsed.serviceCentersVisible),
    truckHotspotsVisible: Boolean(parsed.truckHotspotsVisible),
    trafficVolumeVisible: Boolean(parsed.trafficVolumeVisible),
    powerGridCapacityVisible: Boolean(parsed.powerGridCapacityVisible),
    powerGridHeatmapVisible: Boolean(parsed.powerGridHeatmapVisible),
    substationsVisible: Boolean(parsed.substationsVisible),
    utilitiesVisible: Boolean(parsed.utilitiesVisible),
    sraVisible: Boolean(parsed.sraVisible),
    countiesVisible: Boolean(parsed.countiesVisible),
    overburdenedVisible: Boolean(parsed.overburdenedVisible),
    calEnviroScreenVisible: Boolean(parsed.calEnviroScreenVisible),
    calEnviroScreenMinPercentile: clampPercentile(parsed.calEnviroScreenMinPercentile),
    interactiveTopLayerId,
  };
};

const readRegulatorMapLayerPrefs = () => {
  if (typeof window === 'undefined') return DEFAULT_REGULATOR_MAP_LAYER_PREFS;
  try {
    const stored = localStorage.getItem(REGULATOR_MAP_LAYER_PREFS_KEY);
    if (!stored) return DEFAULT_REGULATOR_MAP_LAYER_PREFS;
    const parsed = JSON.parse(stored);
    return normalizeRegulatorMapLayerPrefs(parsed);
  } catch (_) {
    return DEFAULT_REGULATOR_MAP_LAYER_PREFS;
  }
};

/** Web Mercator: meters per pixel at given latitude and zoom (256px tile at z0). */
function metersPerPixel(latDeg, zoom) {
  const latRad = (latDeg * Math.PI) / 180;
  return (156543.03392 * Math.cos(latRad)) / 2 ** zoom;
}

/** Nice distances based on imperial (ft/mi), then converted to meters for bar length. */
const FT_TO_M = 0.3048;
const MI_TO_M = 1609.344;
const NICE_DISTANCES_M = [
  ...[50, 100, 250, 500, 1000, 2000, 5000].map((ft) => ft * FT_TO_M),
  ...[0.25, 0.5, 1, 2, 5, 10, 25, 50, 100].map((mi) => mi * MI_TO_M),
].sort((a, b) => a - b);

const MAX_SCALE_BAR_PX = 120;
const M_TO_FT = 3.28084;
const M_TO_MI = 1 / 1609.344;

function formatScaleLabel(meters, imperial) {
  if (imperial) {
    const mi = meters * M_TO_MI;
    if (mi >= 0.1) return `${mi >= 1 ? Math.round(mi) : mi.toFixed(2)} mi`;
    const ft = Math.round(meters * M_TO_FT);
    if (ft >= 1000) return `${(ft / 1000).toFixed(1)} kft`;
    return `${ft} ft`;
  }
  return meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${Math.round(meters)} m`;
}

function MapScaleBar({ zoom, centerLat, targetBarWidthPx = 100, imperial = true, onUnitToggle }) {
  const mpp = metersPerPixel(centerLat, zoom);
  const maxDistanceM = mpp * MAX_SCALE_BAR_PX;
  const distanceAtTarget = mpp * targetBarWidthPx;
  const targetM = Math.min(distanceAtTarget * 1.4, maxDistanceM);
  let chosenM = NICE_DISTANCES_M[0];
  for (let i = NICE_DISTANCES_M.length - 1; i >= 0; i--) {
    if (NICE_DISTANCES_M[i] <= targetM) {
      chosenM = NICE_DISTANCES_M[i];
      break;
    }
  }
  const barWidthPx = Math.round(chosenM / mpp);
  const label = formatScaleLabel(chosenM, imperial);

  return (
    <div
      className="absolute bottom-6 left-6 z-10 flex flex-col items-start gap-1 bg-white/90 backdrop-blur-sm rounded shadow border border-gray-200 px-2.5 py-1.5"
      aria-label={`Scale: ${label}`}
    >
      {/* Thin black bar with vertical end caps */}
      <div className="flex items-center" style={{ height: 10 }}>
        <div
          className="bg-black flex-shrink-0"
          style={{ width: 1, height: 8, alignSelf: 'center' }}
          aria-hidden
        />
        <div
          className="bg-black flex-shrink-0"
          style={{ width: barWidthPx, height: 1 }}
          aria-hidden
        />
        <div
          className="bg-black flex-shrink-0"
          style={{ width: 1, height: 8, alignSelf: 'center' }}
          aria-hidden
        />
      </div>
      <div className="flex items-center gap-2 w-full">
        <span className="text-xs font-medium text-gray-800 tabular-nums">{label}</span>
        {onUnitToggle && (
          <button
            type="button"
            onClick={onUnitToggle}
            className="text-sm text-gray-500 hover:text-gray-800 focus:outline-none rounded p-0.5 leading-none"
            title={imperial ? 'Switch to metric (km/m)' : 'Switch to imperial (mi/ft)'}
            aria-label={imperial ? 'Switch to metric' : 'Switch to imperial'}
          >
            ⇄
          </button>
        )}
      </div>
    </div>
  );
}

const formatMetric = (v, suffix = '', options = {}) => {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'string') return v;
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  if (options.integer) return `${Math.round(n)}${suffix}`;
  if (options.decimals != null) return `${n.toFixed(options.decimals)}${suffix}`;
  return `${n}${suffix}`;
};

/**
 * Distance display: feet (with thousands separator) below 0.25 mi (1,320 ft),
 * switching to miles above that threshold so large distances stay readable.
 */
const formatDistance = (value) => {
  if (value == null || value === '') return '—';
  const ft = Number(value);
  if (!Number.isFinite(ft)) return '—';
  const MILE_IN_FEET = 5280;
  if (ft >= 0.25 * MILE_IN_FEET) {
    const mi = ft / MILE_IN_FEET;
    return `${mi.toFixed(2).replace(/\.?0+$/, '')} mi`;
  }
  return `${Math.round(ft).toLocaleString()} ft`;
};

/** Stall counts: show — when not available (null/undefined/0). */
const formatStallMetric = (v) => {
  if (v == null || v === '' || Number(v) === 0) return '—';
  const n = Number(v);
  return Number.isNaN(n) ? '—' : String(Math.round(n));
};

/** Get first defined value from object with candidate keys (for circuit MVT properties). */
const getCircuitProp = (props, ...keys) => {
  if (!props) return null;
  for (const k of keys) {
    const v = props[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return null;
};

const formatMwNumber = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return null;
  const s = num.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
  return s || '0';
};

const formatKwTextToMw = (text) => {
  if (text == null) return null;
  const raw = String(text).trim();
  if (!raw) return null;
  const nums = raw.match(/(\d+(?:,\d+)?(?:\.\d+)?)/g);
  const values = (nums || []).map((x) => Number(String(x).replace(/,/g, ''))).filter((v) => Number.isFinite(v));
  if (values.length === 0) return null;

  // ">7500" => "7.5+ MW"
  if (raw.includes('>')) {
    const low = formatMwNumber(values[0] / 1000);
    return low ? `${low}+ MW` : null;
  }

  // "5500-6000" => "5.5-6 MW"
  if (raw.includes('-') && values.length >= 2) {
    const a = formatMwNumber(values[0] / 1000);
    const b = formatMwNumber(values[1] / 1000);
    return a && b ? `${a}-${b} MW` : null;
  }

  // "5500" => "5.5 MW"
  const single = formatMwNumber(values[0] / 1000);
  return single ? `${single} MW` : null;
};

const formatHostingCapacityMw = (props) => {
  const utility = getCircuitProp(props, 'utility', 'Utility');
  const range = getCircuitProp(props, 'capacity_range_kw', 'capacity_kw_range', 'capacity_range');
  const numeric = getCircuitProp(props, 'capacity', 'capacity_kw', 'hosting_capacity');

  // LADWP often provides a string range (e.g. "5500-6000"); prefer showing that when available.
  if (String(utility || '').toUpperCase() === 'LADWP' && range != null) {
    const mw = formatKwTextToMw(range);
    if (mw) return mw;
  }

  if (numeric != null) {
    const n = Number(numeric);
    if (Number.isFinite(n)) {
      const mw = formatMwNumber(n / 1000);
      return mw ? `${mw} MW` : '—';
    }
    const mw = formatKwTextToMw(numeric);
    if (mw) return mw;
  }

  if (range != null) {
    const mw = formatKwTextToMw(range);
    if (mw) return mw;
  }

  return '—';
};

const normalizeCircuitSelectionValue = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

const normalizeCircuitUtilityValue = (value) => {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return '';
  if (raw === 'sce' || raw.includes('southern california edison')) return 'sce';
  if (raw.includes('ladwp') || (raw.includes('los angeles') && raw.includes('water') && raw.includes('power'))) {
    return 'ladwp';
  }
  return normalizeCircuitSelectionValue(raw);
};

const supportsCircuitMetricsForUtility = (value) => {
  const normalized = normalizeCircuitUtilityValue(value);
  return normalized === 'sce' || normalized === 'ladwp';
};

const buildCircuitSelection = ({ utility = null, circuitName = null, circuitId = null } = {}) => {
  const normalizedName = normalizeCircuitSelectionValue(circuitName);
  const normalizedId = normalizeCircuitSelectionValue(circuitId);
  if (!normalizedName && !normalizedId) return null;
  return { utility, circuitName, circuitId };
};

const getCircuitSelectionFromProps = (props = {}) =>
  buildCircuitSelection({
    utility: getCircuitProp(props, 'utility', 'Utility'),
    circuitName: getCircuitProp(props, 'circuit_name', 'Circuit_Name', 'name'),
    circuitId: getCircuitProp(props, 'circuit_id', 'Circuit_ID', 'id'),
  });

// Split 'Highland 66/12 kV' -> { name: 'Highland', bank: '66/12 kV' }.
const splitSubstationNameBank = (full) => {
  const s = String(full ?? '').trim();
  if (!s) return { name: 'N/A', bank: 'N/A' };
  const idx = s.search(/\d/);
  if (idx <= 0) return { name: s, bank: 'N/A' };
  return { name: s.slice(0, idx).trim() || s, bank: s.slice(idx).trim() };
};

const formatSubstationMwValue = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return `${num.toFixed(2).replace(/\.?0+$/, '')} MW`;
};

// Per-bank outlook entries anchored on the current calendar year (current year
// first, then the following years that exist in the series).
const substationBankOutlookEntries = (bank = {}) => {
  const outlook = bank.capacity_multiyear || {};
  const planYear = Number(outlook.plan_year);
  const series = Array.isArray(outlook.estimated_available) ? outlook.estimated_available : [];
  const entries = [];
  outlookIndices(planYear, series.length).forEach((i) => {
    const mw = formatSubstationMwValue(series[i]);
    if (mw && Number.isFinite(planYear)) entries.push({ year: planYear + i, mw });
  });
  return entries;
};

const circuitsMatch = (left, right) => {
  if (!left || !right) return false;
  const leftUtility = normalizeCircuitUtilityValue(left.utility);
  const rightUtility = normalizeCircuitUtilityValue(right.utility);
  if (leftUtility && rightUtility && leftUtility !== rightUtility) return false;

  const leftId = normalizeCircuitSelectionValue(left.circuitId);
  const rightId = normalizeCircuitSelectionValue(right.circuitId);
  if (leftId && rightId) return leftId === rightId;

  const leftName = normalizeCircuitSelectionValue(left.circuitName);
  const rightName = normalizeCircuitSelectionValue(right.circuitName);
  if (leftName && rightName) return leftName === rightName;

  return false;
};

const getWarehouseProp = (warehouse, ...keys) => {
  if (!warehouse) return null;
  for (const key of keys) {
    const direct = warehouse[key];
    if (direct !== undefined && direct !== null && direct !== '') return direct;
    const nested = warehouse.properties?.[key];
    if (nested !== undefined && nested !== null && nested !== '') return nested;
  }
  return null;
};

const formatSquareFootage = (value) => {
  if (value == null || value === '') return 'N/A';
  const numeric = Number(String(value).replace(/,/g, ''));
  if (!Number.isFinite(numeric)) return String(value);
  return Math.round(numeric).toLocaleString();
};

const formatTruckStopType = (value) => {
  if (!value) return 'N/A';
  if (value === 'intermodal_terminal') return 'Intermodal Terminal';
  if (value === 'service_center') return 'Service Center';
  return value;
};

const getTruckStopTooltipTitle = (value) => {
  if (value === 'intermodal_terminal') return 'Intermodal Terminal Information';
  if (value === 'service_center') return 'Service Center Information';
  return 'Truck Stop Information';
};

/** Collapsible wrapper for a metrics section. Expanded by default. */
function CollapsibleSection({ title, titleClass, children }) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div className="mb-4 last:mb-0">
      <button
        type="button"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setCollapsed((prev) => !prev);
        }}
        className={`${titleClass} flex w-full items-center justify-between gap-2 select-none cursor-pointer`}
        aria-expanded={!collapsed}
      >
        <span>{title}</span>
        <FaChevronRight
          className={`flex-shrink-0 transition-transform duration-150 ${collapsed ? '' : 'rotate-90'}`}
          size={10}
        />
      </button>
      {!collapsed && children}
    </div>
  );
}

/** Renders metrics details for a location in a readable, sectioned layout */
function CapacityAssessmentBadge({ status }) {
  const meta = getAssessmentMeta(status);
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${meta.badgeClass}`}>
      {meta.label}
    </span>
  );
}

const formatMw = (value) => {
  if (value == null) return '—';
  const n = Number(value);
  return Number.isNaN(n) ? '—' : `${n.toFixed(2)} MW`;
};

const formatAssessmentAvailableMw = (assessment) => {
  const n = Number(assessment?.available_mw);
  if (!Number.isFinite(n)) return null;
  const bindingLabel = formatBindingConstraint(assessment?.binding_constraint);
  const binding = bindingLabel ? ` (${bindingLabel})` : '';
  return `Available ${n.toFixed(1)} MW${binding}`;
};

const buildAssessmentTooltip = (assessment) => {
  const meta = getAssessmentMeta(assessment?.status);
  const label = assessment?.status === 'beyond_horizon' ? 'Beyond horizon' : meta.label;
  const details = [meta.tooltipSummary];
  const available = formatAssessmentAvailableMw(assessment);
  if (available) details.push(available);
  return `${label} - ${details.join(' ')}`;
};

// Per-location planned deployment inputs + derived capacity assessment. Editing
// the expected values only PATCHes the location (no metrics recompute needed);
// the backend re-derives the assessment from the already-stored outlook.
function CapacityAssessmentEditor({ location, onSave }) {
  const [editing, setEditing] = useState(false);
  const [capacityDraft, setCapacityDraft] = useState('');
  const [yearDraft, setYearDraft] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEditing(false);
    setError(null);
  }, [location?.id]);

  if (!location) return null;

  const assessment = location.capacity_assessment || null;
  const sectionTitleClass = 'text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 pb-1 border-b border-gray-200';
  const rowClass = 'flex justify-between gap-4 py-1 text-sm';
  const labelClass = 'text-gray-600 flex-shrink-0';
  const valueClass = 'text-gray-900 font-medium text-right tabular-nums';

  const startEditing = () => {
    setCapacityDraft(location.expected_capacity_mw != null ? String(location.expected_capacity_mw) : '');
    setYearDraft(location.expected_year != null ? String(location.expected_year) : '');
    setError(null);
    setEditing(true);
  };

  const handleSave = async () => {
    const capStr = capacityDraft.trim();
    const yearStr = yearDraft.trim();
    const payload = {};
    if (capStr === '') {
      payload.expected_capacity_mw = null;
    } else {
      const cap = Number(capStr);
      if (Number.isNaN(cap) || cap < 0) {
        setError('Enter a valid non-negative capacity (MW).');
        return;
      }
      payload.expected_capacity_mw = cap;
    }
    if (yearStr === '') {
      payload.expected_year = null;
    } else {
      const yr = Number(yearStr);
      if (!Number.isInteger(yr) || yr < 2000 || yr > 2100) {
        setError('Enter a valid year (2000–2100).');
        return;
      }
      payload.expected_year = yr;
    }
    setSaving(true);
    setError(null);
    const ok = await onSave(payload);
    setSaving(false);
    if (ok) setEditing(false);
    else setError('Failed to save. Please try again.');
  };

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between gap-2">
        <span className={sectionTitleClass + ' mb-0 pb-0 border-0'}>Planned Deployment</span>
        {!editing && (
          <button
            type="button"
            onClick={startEditing}
            className="text-xs font-semibold text-blue-700 hover:text-blue-600"
          >
            {location.expected_capacity_mw != null || location.expected_year != null ? 'Edit' : 'Add'}
          </button>
        )}
      </div>
      {editing ? (
        <div className="mt-2 space-y-2">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600 w-40">Expected Capacity (MW)</label>
            <input
              type="number"
              min="0"
              step="any"
              value={capacityDraft}
              onChange={(e) => setCapacityDraft(e.target.value)}
              className="w-28 px-2 py-1 border border-gray-300 rounded text-xs"
              placeholder="e.g. 5"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600 w-40">Expected Year</label>
            <input
              type="number"
              min="2000"
              max="2100"
              step="1"
              value={yearDraft}
              onChange={(e) => setYearDraft(e.target.value)}
              className="w-28 px-2 py-1 border border-gray-300 rounded text-xs"
              placeholder="e.g. 2028"
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-2.5 py-1 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setError(null); }}
              className="text-xs text-gray-500 hover:underline"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-1 space-y-0.5">
          <div className={rowClass}>
            <span className={labelClass}>Expected Capacity</span>
            <span className={valueClass}>{formatMw(location.expected_capacity_mw)}</span>
          </div>
          <div className={rowClass}>
            <span className={labelClass}>Expected Year</span>
            <span className={valueClass}>{location.expected_year != null ? location.expected_year : '—'}</span>
          </div>
          <div className={rowClass}>
            <span className={labelClass}>Assessment</span>
            <span><CapacityAssessmentBadge status={assessment?.status} /></span>
          </div>
          {assessment?.available_mw != null && (
            <div className={rowClass}>
              <span className={labelClass}>
                Available capacity{(() => {
                  const binding = formatBindingConstraint(assessment.binding_constraint);
                  return binding ? ` (${binding})` : '';
                })()}
              </span>
              <span className={valueClass}>{formatMw(assessment.available_mw)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LocationMetricsDetails({ metrics, selectedCircuit, onCircuitToggle }) {
  const [showAdditionalMatrices, setShowAdditionalMatrices] = useState(false);
  if (!metrics) {
    return (
      <p className="text-sm text-gray-500 italic py-2">No metrics computed yet. Run analysis for this location.</p>
    );
  }
  const m = metrics;
  const sectionTitleClass = 'text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 pb-1 border-b border-gray-200';
  const rowClass = 'flex justify-between gap-4 py-1 text-sm';
  const labelClass = 'text-gray-600 flex-shrink-0';
  const valueClass = 'text-gray-900 font-medium text-right tabular-nums';
  const hasUnsupportedUtilityForCircuitMetrics =
    Boolean(String(m.utility_name || '').trim()) && !supportsCircuitMetricsForUtility(m.utility_name);
  const nearestCircuit = buildCircuitSelection({
    utility: m.utility_name,
    circuitName: m.nearest_circuit_name,
  });
  const maxNearbyCircuit = buildCircuitSelection({
    utility: m.utility_name,
    circuitName: m.max_nearby_circuit_name,
  });
  const renderCircuitValue = (circuit) => {
    const displayName = circuit?.circuitName;
    if (!displayName) return <span className={valueClass}>—</span>;
    if (!onCircuitToggle) return <span className={valueClass}>{displayName}</span>;
    const isSelected = circuitsMatch(selectedCircuit, circuit);
    return (
      <button
        type="button"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onCircuitToggle(circuit);
        }}
        className={[
          valueClass,
          'max-w-[200px] transition-colors duration-150',
          isSelected
            ? 'text-blue-600 drop-shadow-[0_0_8px_rgba(59,130,246,0.55)]'
            : 'text-blue-700 hover:text-blue-600 underline decoration-dotted underline-offset-2',
        ].join(' ')}
        title={isSelected ? 'Click to clear selected circuit' : 'Click to highlight this circuit on the map'}
      >
        {displayName}
      </button>
    );
  };

  return (
    <div className="max-h-[30vh] overflow-y-auto pr-1 -mr-1">
      <CollapsibleSection title="Grid & utility" titleClass={sectionTitleClass}>
        <div className="space-y-0.5">
          <div className={rowClass}><span className={labelClass}>Utility</span><span className={valueClass}>{formatMetric(m.utility_name)}</span></div>
          {hasUnsupportedUtilityForCircuitMetrics && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
              Circuit metrics are currently available only for SCE and LADWP.
            </p>
          )}
          <table className="w-full text-sm tabular-nums mt-1">
            <thead>
              <tr className="text-xs text-gray-500">
                <th className="text-left font-medium py-1"> </th>
                <th className="text-right font-medium py-1 px-2">Capacity (MW)</th>
                <th className="text-right font-medium py-1 px-2">Distance</th>
                <th className="text-right font-medium py-1">Name</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="text-gray-600 py-1 whitespace-nowrap">Nearest circuit</td>
                <td className={`${valueClass} px-2`}>{formatMetric(m.nearest_circuit_capacity_mw, '', { decimals: 2 })}</td>
                <td className={`${valueClass} px-2`}>{formatDistance(m.nearest_circuit_distance_ft)}</td>
                <td className="text-right">{renderCircuitValue(nearestCircuit)}</td>
              </tr>
              <tr>
                <td className="text-gray-600 py-1 whitespace-nowrap">Max nearby circuit</td>
                <td className={`${valueClass} px-2`}>{formatMetric(m.max_nearby_circuit_capacity_mw, '', { decimals: 2 })}</td>
                <td className={`${valueClass} px-2`}>{formatDistance(m.max_nearby_circuit_distance_ft)}</td>
                <td className="text-right">{renderCircuitValue(maxNearbyCircuit)}</td>
              </tr>
              <tr>
                <td className="text-gray-600 py-1 whitespace-nowrap">Substation</td>
                <td className={`${valueClass} px-2`}>{formatMetric(m.nearest_substation_capacity_mw, '', { decimals: 2 })}</td>
                <td className={`${valueClass} px-2`}>{formatDistance(m.nearest_substation_distance_ft)}</td>
                <td className={valueClass}>{formatMetric(m.nearest_substation_name)}</td>
              </tr>
              <tr>
                <td className="text-gray-600 py-1 whitespace-nowrap">
                  <span className="inline-flex items-center gap-1">
                    A Bank
                    <ABankInfoTip />
                  </span>
                </td>
                <td className={`${valueClass} px-2`}>{formatMetric(m.nearest_a_bank_capacity_mw, '', { decimals: 2 })}</td>
                <td className={`${valueClass} px-2`}>—</td>
                <td className={valueClass}>{formatMetric(m.nearest_a_bank_name)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-2">
          <button
            type="button"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowAdditionalMatrices((prev) => !prev);
            }}
            className="flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-600"
            aria-expanded={showAdditionalMatrices}
          >
            <FaChevronRight
              className={`transition-transform duration-150 ${showAdditionalMatrices ? 'rotate-90' : ''}`}
              size={10}
            />
            Capacity Outlook
          </button>
          {showAdditionalMatrices && (
            <div className="mt-2">
              <AdditionalMatrices
                nearestAlc={m.nearest_circuit_alc}
                maxAlc={m.max_nearby_circuit_alc}
                nearestName={m.nearest_circuit_name}
                maxName={m.max_nearby_circuit_name}
                substationOutlook={m.nearest_substation_outlook}
                substationName={m.nearest_substation_name}
              />
            </div>
          )}
        </div>
      </CollapsibleSection>
      <CollapsibleSection title="Truck Traffic Volume (PeMS)" titleClass={sectionTitleClass}>
        <div className="grid grid-cols-4 gap-x-2 gap-y-1 text-xs">
          <span className="font-medium text-gray-500 col-span-4">Top 1 by radius</span>
          <span className={labelClass}></span><span className={valueClass}>1 mi</span><span className={valueClass}>2 mi</span><span className={valueClass}>5 mi</span>
          <span className={labelClass}>Hwy Name</span>
          <span className={valueClass}>{formatMetric(m.truck_top1_road_1mi)}</span>
          <span className={valueClass}>{formatMetric(m.truck_top1_road_2mi)}</span>
          <span className={valueClass}>{formatMetric(m.truck_top1_road_5mi)}</span>
          <span className={labelClass}>Traffic Vol. (Truck/Day)</span>
          <span className={valueClass}>{formatMetric(m.truck_top1_max_1mi, '', { integer: true })}</span>
          <span className={valueClass}>{formatMetric(m.truck_top1_max_2mi, '', { integer: true })}</span>
          <span className={valueClass}>{formatMetric(m.truck_top1_max_5mi, '', { integer: true })}</span>
        </div>
      </CollapsibleSection>
      <CollapsibleSection title="Truck Dwelling Index" titleClass={sectionTitleClass}>
        <div className="mt-2 space-y-0.5">
          <div className={rowClass}><span className={labelClass}>Avg stops/day (1 / 2 / 5 mi)</span><span className={valueClass}>{formatMetric(m.avg_stops_per_day_1mi, '', { decimals: 1 })} / {formatMetric(m.avg_stops_per_day_2mi, '', { decimals: 1 })} / {formatMetric(m.avg_stops_per_day_5mi, '', { decimals: 1 })}</span></div>
          <div className={rowClass}><span className={labelClass}>Avg stop duration hr (1 / 2 / 5 mi)</span><span className={valueClass}>{formatMetric(m.avg_stop_duration_hours_1mi, '', { decimals: 1 })} / {formatMetric(m.avg_stop_duration_hours_2mi, '', { decimals: 1 })} / {formatMetric(m.avg_stop_duration_hours_5mi, '', { decimals: 1 })}</span></div>
        </div>
      </CollapsibleSection>
      <CollapsibleSection title="Warehouses" titleClass={sectionTitleClass}>
        <div className="space-y-0.5">
          <div className={rowClass}><span className={labelClass}>Count (1 / 2 / 5 mi)</span><span className={valueClass}>{formatMetric(m.warehouse_count_1mi)} / {formatMetric(m.warehouse_count_2mi)} / {formatMetric(m.warehouse_count_5mi)}</span></div>
          <div className={rowClass}><span className={labelClass}>Acres (1 / 2 / 5 mi)</span><span className={valueClass}>{formatMetric(m.warehouse_acres_1mi, '', { decimals: 1 })} / {formatMetric(m.warehouse_acres_2mi, '', { decimals: 1 })} / {formatMetric(m.warehouse_acres_5mi, '', { decimals: 1 })}</span></div>
        </div>
      </CollapsibleSection>
      <CollapsibleSection title="Intermodal Terminals" titleClass={sectionTitleClass}>
        <div className={rowClass}>
          <span className={labelClass}>Count (1 / 2 / 5 mi)</span>
          <span className={valueClass}>{formatMetric(m.intermodal_count_1mi)} / {formatMetric(m.intermodal_count_2mi)} / {formatMetric(m.intermodal_count_5mi)}</span>
        </div>
      </CollapsibleSection>
      <CollapsibleSection title="MDHD EV Charging Station (open)" titleClass={sectionTitleClass}>
        <div className="space-y-0.5">
          <div className={rowClass}><span className={labelClass}>Sites (1 / 2 / 5 mi)</span><span className={valueClass}>{formatMetric(m.ev_existing_site_count_1mi)} / {formatMetric(m.ev_existing_site_count_2mi)} / {formatMetric(m.ev_existing_site_count_5mi)}</span></div>
          <div className={rowClass}><span className={labelClass}>Stalls (1 / 2 / 5 mi)</span><span className={valueClass}>{formatStallMetric(m.ev_existing_stall_count_1mi)} / {formatStallMetric(m.ev_existing_stall_count_2mi)} / {formatStallMetric(m.ev_existing_stall_count_5mi)}</span></div>
          <div className={rowClass}><span className={labelClass}>Power kW (1 / 2 / 5 mi)</span><span className={valueClass}>{formatMetric(m.ev_existing_power_kw_1mi)} / {formatMetric(m.ev_existing_power_kw_2mi)} / {formatMetric(m.ev_existing_power_kw_5mi)}</span></div>
        </div>
      </CollapsibleSection>
      <CollapsibleSection title="MDHD EV Charging Station (under dev.)" titleClass={sectionTitleClass}>
        <div className="space-y-0.5">
          <div className={rowClass}><span className={labelClass}>Sites (1 / 2 / 5 mi)</span><span className={valueClass}>{formatMetric(m.ev_under_dev_site_count_1mi)} / {formatMetric(m.ev_under_dev_site_count_2mi)} / {formatMetric(m.ev_under_dev_site_count_5mi)}</span></div>
          <div className={rowClass}><span className={labelClass}>Stalls (1 / 2 / 5 mi)</span><span className={valueClass}>{formatStallMetric(m.ev_under_dev_stall_count_1mi)} / {formatStallMetric(m.ev_under_dev_stall_count_2mi)} / {formatStallMetric(m.ev_under_dev_stall_count_5mi)}</span></div>
          <div className={rowClass}><span className={labelClass}>Power kW (1 / 2 / 5 mi)</span><span className={valueClass}>{formatMetric(m.ev_under_dev_power_kw_1mi)} / {formatMetric(m.ev_under_dev_power_kw_2mi)} / {formatMetric(m.ev_under_dev_power_kw_5mi)}</span></div>
        </div>
      </CollapsibleSection>
      <CollapsibleSection title="MDHD EV Charging Station (planned)" titleClass={sectionTitleClass}>
        <div className="space-y-0.5">
          <div className={rowClass}><span className={labelClass}>Sites (1 / 2 / 5 mi)</span><span className={valueClass}>{formatMetric(m.ev_planned_site_count_1mi)} / {formatMetric(m.ev_planned_site_count_2mi)} / {formatMetric(m.ev_planned_site_count_5mi)}</span></div>
          <div className={rowClass}><span className={labelClass}>Stalls (1 / 2 / 5 mi)</span><span className={valueClass}>{formatStallMetric(m.ev_planned_stall_count_1mi)} / {formatStallMetric(m.ev_planned_stall_count_2mi)} / {formatStallMetric(m.ev_planned_stall_count_5mi)}</span></div>
          <div className={rowClass}><span className={labelClass}>Power kW (1 / 2 / 5 mi)</span><span className={valueClass}>{formatMetric(m.ev_planned_power_kw_1mi)} / {formatMetric(m.ev_planned_power_kw_2mi)} / {formatMetric(m.ev_planned_power_kw_5mi)}</span></div>
        </div>
      </CollapsibleSection>
      <CollapsibleSection title="Regulatory Context" titleClass={sectionTitleClass}>
        <div className="space-y-0.5">
          <div className={rowClass}><span className={labelClass}>County</span><span className={valueClass}>{formatMetric(m.county)}</span></div>
          <div className={rowClass}><span className={labelClass}>SCAQMD</span><span className={valueClass}>{formatMetric(m.is_scaqmd)}</span></div>
          <div className={rowClass}><span className={labelClass}>SCAQMD SRA (MONITORING)</span><span className={valueClass}>{formatMetric(m.sra_monitoring, '', { integer: true })}</span></div>
          <div className={rowClass}><span className={labelClass}>EPA CEJST Disadvantaged Communities</span><span className={valueClass}>{formatMetric(m.is_dac)}</span></div>
          <div className={rowClass}><span className={labelClass}>CES4 census tract</span><span className={valueClass}>{formatMetric(m.ces40_tract)}</span></div>
          <div className={rowClass}><span className={labelClass}>CES4 overall percentile</span><span className={valueClass}>{formatMetric(m.ces40_ciscorep, '', { decimals: 1 })}</span></div>
          <div className={rowClass}><span className={labelClass}>CES4 &gt;= 75th percentile</span><span className={valueClass}>{formatMetric(m.ces40_is_ge_75)}</span></div>
          <div className={rowClass}><span className={labelClass}>CES4 &gt;= 90th percentile</span><span className={valueClass}>{formatMetric(m.ces40_is_ge_90)}</span></div>
        </div>
      </CollapsibleSection>
    </div>
  );
}

const downloadTemplate = () => {
  const authToken = localStorage.getItem('authToken');
  const url = API_ENDPOINTS.regulatorAddressImportTemplate;
  const doDownload = (blob) => {
    const u = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = u;
    link.download = 'applicant_addresses_template.xlsx';
    link.click();
    URL.revokeObjectURL(u);
  };
  fetch(url, { headers: authToken ? { Authorization: `Token ${authToken}` } : {} })
    .then((res) => (res.ok ? res.blob() : Promise.reject()))
    .then(doDownload)
    .catch(() => {});
};

const RegulatorMapView = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isLoaded, loadError } = useGoogleMaps();
  const initialLayerPrefsRef = useRef(null);
  if (!initialLayerPrefsRef.current) {
    initialLayerPrefsRef.current = readRegulatorMapLayerPrefs();
  }
  const initialLayerPrefs = initialLayerPrefsRef.current;

  const [addressListCollapsed, setAddressListCollapsed] = useState(false);
  const [map, setMap] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(10);
  const [mapBounds, setMapBounds] = useState(null);
  const [scaleImperial, setScaleImperial] = useState(true);

  // Data layer toggles (10 layers, aligned with charger module)
  const [existingChargersVisible, setExistingChargersVisible] = useState(initialLayerPrefs.existingChargersVisible);
  const [warehousesVisible, setWarehousesVisible] = useState(initialLayerPrefs.warehousesVisible);
  const [intermodalTerminalsVisible, setIntermodalTerminalsVisible] = useState(initialLayerPrefs.intermodalTerminalsVisible);
  const [serviceCentersVisible, setServiceCentersVisible] = useState(initialLayerPrefs.serviceCentersVisible);
  const [truckHotspotsVisible, setTruckHotspotsVisible] = useState(initialLayerPrefs.truckHotspotsVisible);
  const [trafficVolumeVisible, setTrafficVolumeVisible] = useState(initialLayerPrefs.trafficVolumeVisible);
  const [powerGridCapacityVisible, setPowerGridCapacityVisible] = useState(initialLayerPrefs.powerGridCapacityVisible);
  const [powerGridHeatmapVisible, setPowerGridHeatmapVisible] = useState(initialLayerPrefs.powerGridHeatmapVisible);
  const [substationsVisible, setSubstationsVisible] = useState(initialLayerPrefs.substationsVisible);
  const [utilitiesVisible, setUtilitiesVisible] = useState(initialLayerPrefs.utilitiesVisible);
  const [sraVisible, setSraVisible] = useState(initialLayerPrefs.sraVisible);
  const [countiesVisible, setCountiesVisible] = useState(initialLayerPrefs.countiesVisible);
  const [overburdenedVisible, setOverburdenedVisible] = useState(initialLayerPrefs.overburdenedVisible);
  const [calEnviroScreenVisible, setCalEnviroScreenVisible] = useState(initialLayerPrefs.calEnviroScreenVisible);
  const [calEnviroScreenMinPercentile, setCalEnviroScreenMinPercentile] = useState(initialLayerPrefs.calEnviroScreenMinPercentile);
  const [interactiveTopLayerId, setInteractiveTopLayerId] = useState(
    initialLayerPrefs.interactiveTopLayerId
  );

  const [isDataLayersOpen, setIsDataLayersOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [mapLocations, setMapLocations] = useState([]);
  const [locationsTotal, setLocationsTotal] = useState(0);
  const [locationsPage, setLocationsPage] = useState(1);
  const [locationsPageSize] = useState(50);
  const [locationsOrderBy, setLocationsOrderBy] = useState('created_at'); // 'site_name' | 'address' | 'created_at'
  const [locationsOrderDir, setLocationsOrderDir] = useState('desc'); // 'asc' | 'desc'
  const [orderByDropdownOpen, setOrderByDropdownOpen] = useState(false);
  const orderByDropdownRef = useRef(null);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [locationsUpdatingVisible, setLocationsUpdatingVisible] = useState(false);
  const [showAssessmentDots, setShowAssessmentDots] = useState(true);
  const locationsUpdatingShowTimerRef = useRef(null);
  const locationsUpdatingHideTimerRef = useRef(null);
  const locationsFetchSeqRef = useRef(0);
  const locationsAbortRef = useRef(null);
  const warehousesFetchSeqRef = useRef(0);
  const warehousesAbortRef = useRef(null);
  const [runStatus, setRunStatus] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [cancelRunLoading, setCancelRunLoading] = useState(false);
  const [awaitingMapPick, setAwaitingMapPick] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [addAddressError, setAddAddressError] = useState(null);
  const [addAddressLoading, setAddAddressLoading] = useState(false);
  const [addLocationSubmitting, setAddLocationSubmitting] = useState(false);
  // After address search: show tooltip at position with "Add to list"
  const [addLocationTooltip, setAddLocationTooltip] = useState(null);
  const statusPollingRef = useRef(null);
  const addLocationInFlightRef = useRef(false);
  const uploadInFlightRef = useRef(false);
  const addLocationsBlockedRef = useRef(false);
  const autocompleteInputRef = useRef(null);
  const autocompleteInstanceRef = useRef(null);
  const autocompleteStyleInjectedRef = useRef(false);
  const templateInfoRef = useRef(null);
  const [templateInfoOpen, setTemplateInfoOpen] = useState(false);
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);
  const [deleteLocationsLoading, setDeleteLocationsLoading] = useState(false);
  const [updateMetricsLoading, setUpdateMetricsLoading] = useState(false);
  const [updateCompleteCount, setUpdateCompleteCount] = useState(0);
  const [updateCompleteOpen, setUpdateCompleteOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const deleteCancelButtonRef = useRef(null);
  const pendingManualUpdateCountRef = useRef(null);
  // Circuit-data staleness (locations computed against an older circuit dataset)
  const [circuitStaleness, setCircuitStaleness] = useState({ count: 0, locationIds: [] });
  // Map marker popup: selected location and whether details are expanded
  const [selectedMapLocation, setSelectedMapLocation] = useState(null);
  const [mapLocationDetailsExpanded, setMapLocationDetailsExpanded] = useState(false);
  const [editingSiteNameId, setEditingSiteNameId] = useState(null);
  const [editingSiteNameValue, setEditingSiteNameValue] = useState('');
  const [savingSiteNameId, setSavingSiteNameId] = useState(null);
  // Circuit click tooltip (style aligned with ChargingStationMapView)
  const [circuitTooltip, setCircuitTooltip] = useState(null);
  const [selectedCircuit, setSelectedCircuit] = useState(null);
  // Substation click tooltip + highlight (mirrors the circuit interaction)
  const [substationTooltip, setSubstationTooltip] = useState(null);
  const [selectedSubstation, setSelectedSubstation] = useState(null);
  // Charging station (MHD) click tooltip — same content as ChargerPlan module
  const [chargingStationTooltip, setChargingStationTooltip] = useState(null);
  // Warehouse click tooltip
  const [warehouseTooltip, setWarehouseTooltip] = useState(null);
  // Intermodal / service center click tooltip
  const [truckStopTooltip, setTruckStopTooltip] = useState(null);
  // SRA click tooltip
  const [sraTooltip, setSraTooltip] = useState(null);

  // Layer data cache (lazy-loaded, not cleared when layer is turned off)
  const [mhdInfrastructure, setMhdInfrastructure] = useState([]);
  const [substations, setSubstations] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [truckStops, setTruckStops] = useState([]);
  const [trafficData, setTrafficData] = useState([]);
  const [utilitiesData, setUtilitiesData] = useState([]);
  const [sraData, setSraData] = useState([]);
  const [countiesData, setCountiesData] = useState([]);
  const [disadvantagedCommunities, setDisadvantagedCommunities] = useState([]);
  const [calEnviroScreen, setCalEnviroScreen] = useState([]);

  const [mhdLoading, setMhdLoading] = useState(false);
  const [substationsLoading, setSubstationsLoading] = useState(false);
  const [warehousesLoading, setWarehousesLoading] = useState(false);
  const [truckStopsLoading, setTruckStopsLoading] = useState(false);
  const [trafficLoading, setTrafficLoading] = useState(false);
  const [utilitiesLoading, setUtilitiesLoading] = useState(false);
  const [sraLoading, setSraLoading] = useState(false);
  const [countiesLoading, setCountiesLoading] = useState(false);
  const [disadvantagedLoading, setDisadvantagedLoading] = useState(false);
  const [calEnviroScreenLoading, setCalEnviroScreenLoading] = useState(false);

  const geocoderRef = useRef(null);
  const fileInputRef = useRef(null);

  const authToken = useMemo(() => localStorage.getItem('authToken'), []);

  useEffect(() => {
    try {
      localStorage.setItem(
        REGULATOR_MAP_LAYER_PREFS_KEY,
        JSON.stringify({
          existingChargersVisible,
          warehousesVisible,
          intermodalTerminalsVisible,
          serviceCentersVisible,
          truckHotspotsVisible,
          trafficVolumeVisible,
          powerGridCapacityVisible,
          powerGridHeatmapVisible,
          substationsVisible,
          utilitiesVisible,
          sraVisible,
          countiesVisible,
          overburdenedVisible,
          calEnviroScreenVisible,
          calEnviroScreenMinPercentile: clampPercentile(calEnviroScreenMinPercentile),
          interactiveTopLayerId,
        })
      );
    } catch (_) {
      // no-op
    }
  }, [
    existingChargersVisible,
    warehousesVisible,
    intermodalTerminalsVisible,
    serviceCentersVisible,
    truckHotspotsVisible,
    trafficVolumeVisible,
    powerGridCapacityVisible,
    powerGridHeatmapVisible,
    substationsVisible,
    utilitiesVisible,
    sraVisible,
    countiesVisible,
    overburdenedVisible,
    calEnviroScreenVisible,
    calEnviroScreenMinPercentile,
    interactiveTopLayerId,
  ]);

  const stopStatusPolling = useCallback(() => {
    if (statusPollingRef.current) {
      clearInterval(statusPollingRef.current);
      statusPollingRef.current = null;
    }
  }, []);

  const shouldContinuePolling = useCallback((statusData) => {
    if (!statusData || typeof statusData !== 'object') return false;
    const pendingCount = Number(statusData.pending_count || 0);
    const runningCount = Number(statusData.running_count || 0);
    if (pendingCount + runningCount > 0) return true;
    return statusData.status === 'running';
  }, []);

  const buildLocationsQueryParams = useCallback((page, pageSize) => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('page_size', String(pageSize));
    if (searchTerm.trim()) params.set('search', searchTerm.trim());
    if (locationsOrderBy) {
      params.set('order_by', locationsOrderBy);
      params.set('order_dir', locationsOrderDir);
    }
    return params;
  }, [searchTerm, locationsOrderBy, locationsOrderDir]);

  const fetchLocations = useCallback(async () => {
    if (!authToken) return;
    const seq = locationsFetchSeqRef.current + 1;
    locationsFetchSeqRef.current = seq;
    try {
      locationsAbortRef.current?.abort?.();
    } catch {
      // no-op
    }
    const controller = new AbortController();
    locationsAbortRef.current = controller;

    setLocationsLoading(true);
    try {
      const params = buildLocationsQueryParams(locationsPage, locationsPageSize);
      const { data } = await axios.get(
        `${API_ENDPOINTS.regulatorLocations}?${params}`,
        { headers: { Authorization: `Token ${authToken}` }, signal: controller.signal }
      );
      if (controller.signal.aborted) return;
      if (seq !== locationsFetchSeqRef.current) return;
      setLocations(data.results || []);
      setLocationsTotal(data.count ?? 0);
    } catch (e) {
      // Abort or outdated requests shouldn't clear the list (prevents flicker)
      if (controller.signal.aborted) return;
      if (seq !== locationsFetchSeqRef.current) return;
      // Keep existing list on error; just stop the loading indicator.
    } finally {
      if (seq === locationsFetchSeqRef.current) setLocationsLoading(false);
    }
  }, [authToken, locationsPage, locationsPageSize, buildLocationsQueryParams]);

  const fetchAllLocationsForMap = useCallback(async () => {
    if (!authToken) return;
    try {
      const firstParams = new URLSearchParams();
      firstParams.set('page', '1');
      firstParams.set('page_size', String(MAP_FETCH_PAGE_SIZE));
      firstParams.set('lite', '1');
      const firstResp = await axios.get(
        `${API_ENDPOINTS.regulatorLocations}?${firstParams}`,
        { headers: { Authorization: `Token ${authToken}` } }
      );
      const firstData = firstResp.data || {};
      const total = Number(firstData.count || 0);
      const pages = Math.max(1, Math.ceil(total / MAP_FETCH_PAGE_SIZE));
      const allResults = Array.isArray(firstData.results) ? [...firstData.results] : [];

      if (pages > 1) {
        const pageRequests = [];
        for (let page = 2; page <= pages; page += 1) {
          const params = new URLSearchParams();
          params.set('page', String(page));
          params.set('page_size', String(MAP_FETCH_PAGE_SIZE));
          params.set('lite', '1');
          pageRequests.push(
            axios.get(`${API_ENDPOINTS.regulatorLocations}?${params}`, {
              headers: { Authorization: `Token ${authToken}` },
            })
          );
        }
        const pageResponses = await Promise.all(pageRequests);
        pageResponses.forEach(({ data }) => {
          if (Array.isArray(data?.results)) {
            allResults.push(...data.results);
          }
        });
      }
      setMapLocations(allResults);
    } catch {
      setMapLocations([]);
    }
  }, [authToken]);

  const pollStatus = useCallback(async () => {
    if (!authToken) return;
    try {
      const { data } = await axios.get(API_ENDPOINTS.regulatorStatus, {
        headers: { Authorization: `Token ${authToken}` },
      });
      setRunStatus(data);
      if (shouldContinuePolling(data)) return;
      if (pendingManualUpdateCountRef.current != null) {
        if (data?.status === 'completed') {
          setUpdateCompleteCount(pendingManualUpdateCountRef.current);
          setUpdateCompleteOpen(true);
        }
        pendingManualUpdateCountRef.current = null;
      }
      stopStatusPolling();
      fetchLocations();
      fetchAllLocationsForMap();
    } catch {
      setRunStatus(null);
      stopStatusPolling();
    }
  }, [authToken, fetchLocations, fetchAllLocationsForMap, shouldContinuePolling, stopStatusPolling]);

  const startStatusPolling = useCallback(() => {
    stopStatusPolling();
    statusPollingRef.current = setInterval(pollStatus, 2000);
  }, [pollStatus, stopStatusPolling]);

  const handleCancelRun = useCallback(async () => {
    if (!authToken || cancelRunLoading) return;
    setCancelRunLoading(true);
    try {
      await axios.post(
        API_ENDPOINTS.regulatorCancel,
        {},
        { headers: { Authorization: `Token ${authToken}` } }
      );
      await pollStatus();
    } catch {
      // Best-effort; the next poll/status refresh will reconcile.
    } finally {
      setCancelRunLoading(false);
    }
  }, [authToken, cancelRunLoading, pollStatus]);

  useEffect(() => {
    if (!authToken) return;
    fetchLocations();
    fetchAllLocationsForMap();
  }, [authToken, fetchLocations, fetchAllLocationsForMap]);

  useEffect(() => {
    if (!authToken) return;
    const statusUrl = API_ENDPOINTS.regulatorStatus;
    const checkOnce = async () => {
      try {
        const { data } = await axios.get(statusUrl, {
          headers: { Authorization: `Token ${authToken}` },
        });
        setRunStatus(data);
        if (shouldContinuePolling(data)) {
          startStatusPolling();
        }
      } catch {
        setRunStatus(null);
      }
    };
    checkOnce();
    return () => {
      stopStatusPolling();
    };
  }, [authToken, startStatusPolling, shouldContinuePolling, stopStatusPolling]);

  useEffect(() => {
    const t = setTimeout(() => fetchLocations(), 300);
    return () => clearTimeout(t);
  }, [locationsPage, searchTerm, locationsOrderBy, locationsOrderDir, fetchLocations]);

  const openDeleteConfirm = useCallback(() => {
    if (selectedLocationIds.length === 0 || deleteLocationsLoading) return;
    setUploadError(null);
    setDeleteConfirmOpen(true);
  }, [selectedLocationIds, deleteLocationsLoading]);

  useEffect(() => {
    if (deleteConfirmOpen && selectedLocationIds.length === 0) setDeleteConfirmOpen(false);
  }, [deleteConfirmOpen, selectedLocationIds.length]);

  useEffect(() => {
    if (!deleteConfirmOpen) return;
    deleteCancelButtonRef.current?.focus?.();
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !deleteLocationsLoading) setDeleteConfirmOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [deleteConfirmOpen, deleteLocationsLoading]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedLocationIds.length === 0 || !authToken || deleteLocationsLoading) return;
    setDeleteLocationsLoading(true);
    setUploadError(null);
    try {
      await Promise.all(
        selectedLocationIds.map((id) =>
          axios.delete(API_ENDPOINTS.regulatorLocationDetail(id), {
            headers: { Authorization: `Token ${authToken}` },
          })
        )
      );
      setSelectedLocationIds([]);
      setDeleteConfirmOpen(false);
      fetchLocations();
      fetchAllLocationsForMap();
    } catch (e) {
      setUploadError(e.response?.data?.error || 'Failed to delete some locations.');
    } finally {
      setDeleteLocationsLoading(false);
    }
  }, [selectedLocationIds, authToken, fetchLocations, fetchAllLocationsForMap, deleteLocationsLoading]);

  const handleUpdateSelectedMetrics = useCallback(async () => {
    if (
      selectedLocationIds.length === 0 ||
      !authToken ||
      updateMetricsLoading ||
      runStatus?.status === 'running'
    ) {
      return;
    }
    setUpdateMetricsLoading(true);
    setUploadError(null);
    try {
      await axios.post(
        API_ENDPOINTS.regulatorProcess,
        { location_ids: selectedLocationIds },
        { headers: { Authorization: `Token ${authToken}`, 'Content-Type': 'application/json' } }
      );
      pendingManualUpdateCountRef.current = selectedLocationIds.length;
      setUpdateCompleteOpen(false);
      setRunStatus((prev) => ({
        ...(prev && typeof prev === 'object' ? prev : {}),
        status: 'running',
        run_total: selectedLocationIds.length,
        run_processed: 0,
      }));
      startStatusPolling();
    } catch (e) {
      setUploadError(e.response?.data?.error || 'Failed to start metrics update.');
    } finally {
      setUpdateMetricsLoading(false);
    }
  }, [selectedLocationIds, authToken, updateMetricsLoading, runStatus?.status, startStatusPolling]);

  const checkCircuitStaleness = useCallback(async () => {
    if (!authToken) return;
    try {
      const { data } = await axios.get(API_ENDPOINTS.regulatorCircuitStaleness, {
        headers: { Authorization: `Token ${authToken}` },
      });
      setCircuitStaleness({
        count: data?.stale_count || 0,
        locationIds: data?.stale_location_ids || [],
      });
    } catch {
      // Non-fatal: leave any previous staleness state untouched.
    }
  }, [authToken]);

  // Re-check staleness on mount and whenever a metrics run finishes.
  useEffect(() => {
    if (runStatus?.status === 'running') return;
    checkCircuitStaleness();
  }, [authToken, runStatus?.status, checkCircuitStaleness]);

  const handleRefreshStaleCircuits = useCallback(async () => {
    const ids = circuitStaleness.locationIds;
    if (!ids.length || !authToken || runStatus?.status === 'running') return;
    setUploadError(null);
    try {
      await axios.post(
        API_ENDPOINTS.regulatorProcess,
        { location_ids: ids },
        { headers: { Authorization: `Token ${authToken}`, 'Content-Type': 'application/json' } }
      );
      pendingManualUpdateCountRef.current = ids.length;
      setUpdateCompleteOpen(false);
      setRunStatus((prev) => ({
        ...(prev && typeof prev === 'object' ? prev : {}),
        status: 'running',
        run_total: ids.length,
        run_processed: 0,
      }));
      startStatusPolling();
    } catch (e) {
      setUploadError(e.response?.data?.error || 'Failed to start circuit refresh.');
    }
  }, [circuitStaleness.locationIds, authToken, runStatus?.status, startStatusPolling]);

  const toggleLocationSelection = useCallback((id) => {
    setSelectedLocationIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const selectAllOnPage = useCallback(() => {
    const ids = locations.map((loc) => loc.id);
    setSelectedLocationIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return [...next];
    });
  }, [locations]);

  const selectAllLocations = useCallback(async () => {
    if (!authToken || selectAllLoading || locationsTotal === 0) return;
    setSelectAllLoading(true);
    setUploadError(null);
    try {
      const pageSize = 200;
      const firstParams = buildLocationsQueryParams(1, pageSize);
      const { data: firstData } = await axios.get(
        `${API_ENDPOINTS.regulatorLocations}?${firstParams}`,
        { headers: { Authorization: `Token ${authToken}` } }
      );
      const allIds = Array.isArray(firstData?.results)
        ? firstData.results.map((loc) => loc?.id).filter((id) => id != null)
        : [];
      const total = Number(firstData?.count || 0);
      const totalPages = Math.max(1, Math.ceil(total / pageSize));

      if (totalPages > 1) {
        const pageRequests = [];
        for (let page = 2; page <= totalPages; page += 1) {
          const params = buildLocationsQueryParams(page, pageSize);
          pageRequests.push(
            axios.get(`${API_ENDPOINTS.regulatorLocations}?${params}`, {
              headers: { Authorization: `Token ${authToken}` },
            })
          );
        }
        const pageResponses = await Promise.all(pageRequests);
        pageResponses.forEach(({ data }) => {
          if (Array.isArray(data?.results)) {
            allIds.push(...data.results.map((loc) => loc?.id).filter((id) => id != null));
          }
        });
      }

      setSelectedLocationIds(Array.from(new Set(allIds)));
    } catch (e) {
      setUploadError(e.response?.data?.error || 'Failed to select all locations.');
    } finally {
      setSelectAllLoading(false);
    }
  }, [authToken, selectAllLoading, locationsTotal, buildLocationsQueryParams]);

  const clearSelection = useCallback(() => setSelectedLocationIds([]), []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (templateInfoRef.current && !templateInfoRef.current.contains(e.target)) {
        setTemplateInfoOpen(false);
      }
    };
    if (templateInfoOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [templateInfoOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (orderByDropdownRef.current && !orderByDropdownRef.current.contains(e.target)) {
        setOrderByDropdownOpen(false);
      }
    };
    if (orderByDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [orderByDropdownOpen]);

  useEffect(() => {
    // Avoid "Updating…" flicker:
    // - Only show if loading lasts >150ms
    // - Once shown, keep visible at least 250ms, and don't hide between back-to-back requests
    if (locationsLoading) {
      if (locationsUpdatingHideTimerRef.current) {
        clearTimeout(locationsUpdatingHideTimerRef.current);
        locationsUpdatingHideTimerRef.current = null;
      }
      if (!locationsUpdatingVisible && !locationsUpdatingShowTimerRef.current) {
        locationsUpdatingShowTimerRef.current = setTimeout(() => {
          locationsUpdatingShowTimerRef.current = null;
          setLocationsUpdatingVisible(true);
        }, 150);
      }
      return () => {};
    }

    if (locationsUpdatingShowTimerRef.current) {
      clearTimeout(locationsUpdatingShowTimerRef.current);
      locationsUpdatingShowTimerRef.current = null;
    }
    if (locationsUpdatingVisible && !locationsUpdatingHideTimerRef.current) {
      locationsUpdatingHideTimerRef.current = setTimeout(() => {
        locationsUpdatingHideTimerRef.current = null;
        setLocationsUpdatingVisible(false);
      }, 250);
    }
    return () => {};
  }, [locationsLoading, locationsUpdatingVisible]);

  useEffect(() => {
    return () => {
      if (locationsUpdatingShowTimerRef.current) clearTimeout(locationsUpdatingShowTimerRef.current);
      if (locationsUpdatingHideTimerRef.current) clearTimeout(locationsUpdatingHideTimerRef.current);
    };
  }, []);

  const metricsProgressPct = useMemo(() => {
    if (!runStatus || runStatus.status !== 'running') return 0;
    const total = runStatus.run_total ?? runStatus.total_count ?? 0;
    const processed = runStatus.run_processed ?? runStatus.processed_count ?? 0;
    if (!total) return 0;
    return Math.min(100, Math.round((Number(processed) / Number(total)) * 100));
  }, [runStatus]);

  const isProcessing = useMemo(() => {
    const pendingCount = Number(runStatus?.pending_count || 0);
    const runningCount = Number(runStatus?.running_count || 0);
    return runStatus?.status === 'running' || pendingCount + runningCount > 0;
  }, [runStatus]);

  const addLocationsBlocked = useMemo(
    () => isProcessing || uploadLoading || addLocationSubmitting || addAddressLoading,
    [isProcessing, uploadLoading, addLocationSubmitting, addAddressLoading]
  );

  useEffect(() => {
    addLocationsBlockedRef.current = addLocationsBlocked;
  }, [addLocationsBlocked]);

  useEffect(() => {
    // If a processing run starts, immediately stop any "add" interaction states.
    if (!isProcessing) return;
    setAwaitingMapPick(false);
    // Keep the tooltip visible (so users can read it) but prevent submitting.
  }, [isProcessing]);

  const applicantAddressesForMap = useMemo(
    () =>
      mapLocations
        .filter((a) => a.latitude != null && a.longitude != null)
        .map((a) => ({
          id: a.id,
          address: a.normalized_address || a.raw_address,
          lat: a.latitude,
          lng: a.longitude,
          location: a,
        })),
    [mapLocations]
  );

  const getLocationPrimaryLabel = useCallback(
    (loc) => (loc?.site_name || '').trim() || loc?.normalized_address || loc?.raw_address || 'Unnamed location',
    []
  );

  const getLocationSecondaryLabel = useCallback((loc) => {
    const siteName = (loc?.site_name || '').trim();
    const address = loc?.normalized_address || loc?.raw_address || '';
    if (!siteName || !address) return '';
    return address;
  }, []);

  // Keep the same pin shape, only change fill color based on selection.
  // Uses a standard 24x24 "pin" path (anchor at bottom-center).
  const applicantMarkerIcons = useMemo(() => {
    if (!isLoaded || !window.google?.maps?.Point) return { blue: undefined, red: undefined };

    const base = {
      // Material "place" pin; visually matches the existing pin style.
      path:
        'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z',
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeOpacity: 1,
      strokeWeight: 2,
      // Increase icon size by 50%
      scale: 2.025,
      anchor: new window.google.maps.Point(12, 24),
    };

    return {
      blue: { ...base, fillColor: 'rgba(15, 190, 242, 0.92)' },
      red: { ...base, fillColor: 'rgba(254, 105, 24, 1.00)' },
    };
  }, [isLoaded]);

  const handleCircuitClick = useCallback((info) => {
    const coord = info?.coordinate;
    const obj = info?.object;
    if (!coord || !obj) return;
    const [lng, lat] = coord;
    const circuit = getCircuitSelectionFromProps(obj.properties || {});
    if (circuit) {
      setSelectedCircuit((prev) => (circuitsMatch(prev, circuit) ? null : circuit));
      setPowerGridCapacityVisible(true);
    }
    setCircuitTooltip({ position: { lat, lng }, circuit: obj });
  }, []);

  const handleSubstationClick = useCallback((object, coordinate) => {
    if (!object || !Array.isArray(coordinate)) return;
    const [lng, lat] = coordinate;
    const position = Array.isArray(object.position) ? object.position : [lng, lat];
    const key = (pos) => (Array.isArray(pos) ? `${Number(pos[0]).toFixed(6)},${Number(pos[1]).toFixed(6)}` : null);
    setSelectedSubstation((prev) => (prev && key(prev.position) === key(position) ? null : object));
    setSubstationTooltip({ position: { lat, lng }, substation: object });
  }, []);

  const handleCircuitToggleFromLocation = useCallback((circuit) => {
    if (!circuit) return;
    setSelectedCircuit((prev) => (circuitsMatch(prev, circuit) ? null : circuit));
    setPowerGridCapacityVisible(true);
  }, []);

  const handleMapBackgroundClick = useCallback(() => {
    if (awaitingMapPick) return;
    setSelectedMapLocation(null);
    setMapLocationDetailsExpanded(false);
    setSelectedCircuit(null);
    setCircuitTooltip(null);
    setSelectedSubstation(null);
    setSubstationTooltip(null);
  }, [awaitingMapPick]);

  const handleChargingStationClick = useCallback((station, event) => {
    if (!station) return;
    const coord = event?.coordinate;
    let lat;
    let lng;
    if (coord && Array.isArray(coord) && coord.length >= 2) {
      lng = coord[0];
      lat = coord[1];
    } else {
      lat = parseFloat(station.lat);
      lng = parseFloat(station.lon);
    }
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    setChargingStationTooltip({ position: { lat, lng }, station });
  }, []);

  const handleSraClick = useCallback((sra, coordinate) => {
    if (!sra || !Array.isArray(coordinate) || coordinate.length < 2) return;
    setSraTooltip({
      position: { lat: coordinate[1], lng: coordinate[0] },
      sra,
    });
  }, []);

  const handleWarehouseClick = useCallback((warehouse, coordinate) => {
    if (!warehouse || !Array.isArray(coordinate) || coordinate.length < 2) return;
    setWarehouseTooltip({
      position: { lat: coordinate[1], lng: coordinate[0] },
      warehouse,
    });
  }, []);

  const handleTruckStopClick = useCallback((truckStop, coordinate) => {
    if (!truckStop || !Array.isArray(coordinate) || coordinate.length < 2) return;
    setTruckStopTooltip({
      position: { lat: coordinate[1], lng: coordinate[0] },
      truckStop,
    });
  }, []);

  const addLocation = useCallback(
    async (payload) => {
      if (addLocationInFlightRef.current) return;
      if (isProcessing) {
        setAddAddressError('Processing is in progress. Please wait until it finishes before adding new locations.');
        return;
      }
      addLocationInFlightRef.current = true;
      setAddLocationSubmitting(true);
      try {
        const { data } = await axios.post(API_ENDPOINTS.regulatorLocations, payload, {
          headers: { Authorization: `Token ${authToken}`, 'Content-Type': 'application/json' },
        });
        fetchLocations();
        fetchAllLocationsForMap();
        if (data.processing_started) {
          setRunStatus({
            status: 'running',
            run_total: data.run_total ?? 1,
            run_processed: 0,
            total_count: 0,
            processed_count: 0,
          });
          startStatusPolling();
        }
      } catch (e) {
        setAddAddressError(e.response?.data?.error || 'Failed to add location.');
      } finally {
        addLocationInFlightRef.current = false;
        setAddLocationSubmitting(false);
      }
    },
    [authToken, fetchLocations, fetchAllLocationsForMap, startStatusPolling, isProcessing]
  );

  const saveSiteName = useCallback(async (locationId) => {
    if (!authToken || !locationId) return;
    setSavingSiteNameId(locationId);
    setAddAddressError(null);
    try {
      await axios.patch(
        API_ENDPOINTS.regulatorLocationDetail(locationId),
        { site_name: editingSiteNameValue.trim() },
        { headers: { Authorization: `Token ${authToken}`, 'Content-Type': 'application/json' } }
      );
      setEditingSiteNameId(null);
      setEditingSiteNameValue('');
      fetchLocations();
      fetchAllLocationsForMap();
    } catch (e) {
      setAddAddressError(e.response?.data?.error || 'Failed to update site name.');
    } finally {
      setSavingSiteNameId(null);
    }
  }, [authToken, editingSiteNameValue, fetchLocations, fetchAllLocationsForMap]);

  const saveExpectedDeployment = useCallback(async (locationId, payload) => {
    if (!authToken || !locationId) return false;
    try {
      const { data } = await axios.patch(
        API_ENDPOINTS.regulatorLocationDetail(locationId),
        payload,
        { headers: { Authorization: `Token ${authToken}`, 'Content-Type': 'application/json' } }
      );
      setSelectedMapLocation((prev) => (prev && prev.id === locationId ? { ...prev, ...data } : prev));
      fetchLocations();
      fetchAllLocationsForMap();
      return true;
    } catch {
      return false;
    }
  }, [authToken, fetchLocations, fetchAllLocationsForMap]);

  const dataLayerGroups = useMemo(
    () => [
      {
        title: 'Charging Infrastructure',
        items: [
          {
            id: 'existingChargers',
            label: 'Charging Stations',
            description: 'MDHD charging stations that are open, under development, or planned (Source: CalSTART).',
            checked: existingChargersVisible,
            onChange: setExistingChargersVisible,
            icon: FiZap,
            iconClass: 'text-amber-500',
          },
        ],
      },
      {
        title: 'Trucking Infrastructure',
        items: [
          {
            id: 'warehouses',
            label: 'Warehouses',
            description: 'Southern California warehouse footprint — Warehouse CITY.',
            checked: warehousesVisible,
            onChange: setWarehousesVisible,
            icon: FiPackage,
            iconClass: 'text-teal-600',
          },
          {
            id: 'intermodalTerminals',
            label: 'Intermodal Terminals',
            description: 'Ports, rail terminals, equipment depots — LoadMatch.',
            checked: intermodalTerminalsVisible,
            onChange: setIntermodalTerminalsVisible,
            icon: FiShare2,
            iconClass: 'text-indigo-500',
          },
          {
            id: 'serviceCenters',
            label: 'Service Centers',
            description: 'Truck service centers (fuel, maintenance, rest).',
            checked: serviceCentersVisible,
            onChange: setServiceCentersVisible,
            icon: FiTool,
            iconClass: 'text-gray-600',
          },
        ],
      },
      {
        title: 'Truck Activity',
        items: [
          {
            id: 'truckHotspots',
            label: 'Truck Hotspots',
            description: 'Aggregated truck parking locations.',
            checked: truckHotspotsVisible,
            onChange: setTruckHotspotsVisible,
            icon: FiMapPin,
            iconClass: 'text-rose-500',
          },
          {
            id: 'trafficVolume',
            label: 'Traffic Volume',
            description: 'Truck traffic along major California corridors — PeMS.',
            checked: trafficVolumeVisible,
            onChange: setTrafficVolumeVisible,
            icon: PiTrafficSignal,
            iconClass: 'text-emerald-600',
          },
        ],
      },
      {
        title: 'Power Grid Information',
        items: [
          {
            id: 'powerGridCapacity',
            label: 'Distribution Circuit Capacity',
            description: 'Distribution circuit hosting capacity — Individual circuits (Zoom in to view).',
            checked: powerGridCapacityVisible,
            onChange: setPowerGridCapacityVisible,
            icon: FiTrendingUp,
            iconClass: 'text-purple-600',
          },
          {
            id: 'powerGridHeatmap',
            label: 'Distribution Circuit Capacity (Heatmap)',
            description: 'Distribution circuit hosting capacity — Heatmap style.',
            checked: powerGridHeatmapVisible,
            onChange: setPowerGridHeatmapVisible,
            icon: FiTrendingUp,
            iconClass: 'text-fuchsia-600',
          },
          {
            id: 'substations',
            label: 'Substations',
            description: 'Substations with available capacity outlook.',
            checked: substationsVisible,
            onChange: setSubstationsVisible,
            icon: FiZap,
            iconClass: 'text-amber-500',
          },
          {
            id: 'utilities',
            label: 'Utilities',
            description: 'Utility service territories.',
            checked: utilitiesVisible,
            onChange: setUtilitiesVisible,
            icon: FiGrid,
            iconClass: 'text-blue-600',
          },
        ],
      },
      {
        title: 'Regulatory Boundaries',
        items: [
          {
            id: 'counties',
            label: 'County Boundaries',
            description: 'US county boundaries — TIGER/Line data.',
            checked: countiesVisible,
            onChange: setCountiesVisible,
            icon: FiMap,
            iconClass: 'text-blue-500',
          },
          {
            id: 'sra',
            label: 'SCAQMD Source Receptor Areas',
            description: 'Source receptor areas with MONITORING classification.',
            checked: sraVisible,
            onChange: setSraVisible,
            icon: FiMap,
            iconClass: 'text-cyan-600',
          },
          {
            id: 'overburdened',
            label: 'EPA CEJST Disadvantaged Communities',
            description: 'Communities defined by the Inflation Reduction Act as disadvantaged.',
            checked: overburdenedVisible,
            onChange: setOverburdenedVisible,
            icon: FiAlertTriangle,
            iconClass: 'text-red-500',
          },
          {
            id: 'calEnviroScreen40',
            label: 'CalEnviroScreen 4.0',
            description: calEnviroScreenLoading
              ? 'Loading CalEnviroScreen 4.0 tracts...'
              : 'California census tracts ranked by cumulative pollution and socioeconomic burden (OEHHA).',
            checked: calEnviroScreenVisible,
            onChange: setCalEnviroScreenVisible,
            icon: FiAlertTriangle,
            iconClass: 'text-amber-600',
            numericFilter: {
              label: 'Percentile',
              min: 0,
              max: 100,
              step: 1,
              value: calEnviroScreenMinPercentile,
              onChange: (value) => {
                const parsed = Number(value);
                if (!Number.isFinite(parsed)) return;
                setCalEnviroScreenMinPercentile(Math.max(0, Math.min(100, parsed)));
              },
            },
          },
        ],
      },
    ],
    [
      existingChargersVisible,
      warehousesVisible,
      intermodalTerminalsVisible,
      serviceCentersVisible,
      truckHotspotsVisible,
      trafficVolumeVisible,
      powerGridCapacityVisible,
      powerGridHeatmapVisible,
      substationsVisible,
      utilitiesVisible,
      sraVisible,
      countiesVisible,
      overburdenedVisible,
      calEnviroScreenVisible,
      calEnviroScreenMinPercentile,
      calEnviroScreenLoading,
    ]
  );

  const activeDataLayerCount = useMemo(
    () =>
      dataLayerGroups.reduce(
        (sum, group) => sum + group.items.filter((item) => item.checked).length,
        0
      ),
    [dataLayerGroups]
  );

  const activeDataLayerOptions = useMemo(
    () =>
      dataLayerGroups
        .flatMap((group) => group.items)
        .filter((item) => item.checked)
        .map((item) => ({ id: item.id, label: item.label })),
    [dataLayerGroups]
  );

  const effectiveInteractiveTopLayerId = useMemo(() => {
    if (interactiveTopLayerId === AUTO_CLICK_PRIORITY_LAYER_ID) {
      return AUTO_CLICK_PRIORITY_LAYER_ID;
    }
    return activeDataLayerOptions.some((item) => item.id === interactiveTopLayerId)
      ? interactiveTopLayerId
      : AUTO_CLICK_PRIORITY_LAYER_ID;
  }, [activeDataLayerOptions, interactiveTopLayerId]);

  useEffect(() => {
    if (interactiveTopLayerId === AUTO_CLICK_PRIORITY_LAYER_ID) return;
    const hasMatchingActiveLayer = activeDataLayerOptions.some(
      (item) => item.id === interactiveTopLayerId
    );
    if (!hasMatchingActiveLayer) {
      setInteractiveTopLayerId(AUTO_CLICK_PRIORITY_LAYER_ID);
    }
  }, [activeDataLayerOptions, interactiveTopLayerId]);

  const toggleAddressListCollapse = () => setAddressListCollapsed((prev) => !prev);

  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  const onMapUnmount = useCallback(() => {
    setMap(null);
  }, []);

  useEffect(() => {
    if (!isLoaded || geocoderRef.current) return;
    initializeGeocoder()
      .then((geocoder) => {
        geocoderRef.current = geocoder;
      })
      .catch(() => {});
  }, [isLoaded]);

  // Google Places Autocomplete for single-address search in toolbar
  useEffect(() => {
    if (!isLoaded || !autocompleteInputRef.current || autocompleteInstanceRef.current) return;
    if (!window.google?.maps?.places?.Autocomplete) return;

    if (!autocompleteStyleInjectedRef.current) {
      const styleTag = document.createElement('style');
      styleTag.id = 'regulator-pac-custom-style';
      styleTag.innerHTML = `
        .pac-container, .pac-item, .pac-item-query {
          font-family: 'Nunito', 'Inter', system-ui, -apple-system, sans-serif !important;
        }
      `;
      document.head.appendChild(styleTag);
      autocompleteStyleInjectedRef.current = true;
    }

    const baseCenter = mapBounds
      ? { lat: (mapBounds.north + mapBounds.south) / 2, lng: (mapBounds.east + mapBounds.west) / 2 }
      : defaultCenter;
    const defaultBounds = {
      north: baseCenter.lat + 0.1,
      south: baseCenter.lat - 0.1,
      east: baseCenter.lng + 0.1,
      west: baseCenter.lng - 0.1,
    };

    const ac = new window.google.maps.places.Autocomplete(autocompleteInputRef.current, {
      bounds: defaultBounds,
      componentRestrictions: { country: 'us' },
      strictBounds: false,
    });
    autocompleteInstanceRef.current = ac;

    const listener = ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      const address = place.formatted_address || place.name || '';
      const loc = place.geometry?.location;
      if (!address || !loc) return;
      const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
      const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      setAddAddressError(null);
      if (map) {
        map.panTo({ lat, lng });
        const zoom = Math.max(15, map.getZoom?.() ?? 15);
        map.setZoom?.(zoom);
      }
      setAddLocationTooltip({
        position: { lat, lng },
        normalizedAddress: address,
        rawAddress: address,
        siteName: '',
      });
    });

    return () => {
      if (listener?.remove) listener.remove();
      autocompleteInstanceRef.current = null;
    };
  }, [isLoaded, map, mapBounds]);

  useEffect(() => {
    if (!map) return;
    const updateBounds = () => {
      const b = map.getBounds();
      if (b) {
        const ne = b.getNorthEast();
        const sw = b.getSouthWest();
        setMapBounds({
          north: ne.lat(),
          south: sw.lat(),
          east: ne.lng(),
          west: sw.lng(),
        });
      }
    };
    updateBounds();
    const listener = map.addListener?.('idle', updateBounds);
    return () => {
      if (listener && typeof listener.remove === 'function') listener.remove();
    };
  }, [map]);

  useEffect(() => {
    if (!map) return;
    const handleZoom = () => {
      const z = map.getZoom();
      if (typeof z === 'number') setCurrentZoom(z);
    };
    const listener = map.addListener?.('zoom_changed', handleZoom);
    handleZoom();
    return () => {
      if (listener && typeof listener.remove === 'function') listener.remove();
    };
  }, [map]);

  // Lazy data fetchers
  const fetchMhd = useCallback(async () => {
    setMhdLoading(true);
    try {
      const response = await axios.get(LAYER_API.getMdhdInfrastructure, {
        headers: authToken ? { Authorization: `Token ${authToken}` } : {},
      });
      setMhdInfrastructure(Array.isArray(response.data) ? response.data : []);
    } catch {
      setMhdInfrastructure([]);
    } finally {
      setMhdLoading(false);
    }
  }, [authToken]);

  const fetchWarehouses = useCallback(async () => {
    if (!authToken || !mapBounds) return;
    const seq = warehousesFetchSeqRef.current + 1;
    warehousesFetchSeqRef.current = seq;
    try {
      warehousesAbortRef.current?.abort?.();
    } catch {
      // no-op
    }
    const controller = new AbortController();
    warehousesAbortRef.current = controller;

    setWarehousesLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('bounds', `${mapBounds.south},${mapBounds.west},${mapBounds.north},${mapBounds.east}`);
      params.append('zoom', String(currentZoom));
      const response = await axios.get(`${LAYER_API.getWarehouses}?${params}`, {
        headers: { Authorization: `Token ${authToken}` },
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      if (seq !== warehousesFetchSeqRef.current) return;
      let list = [];
      if (response.data?.warehouses?.features) list = response.data.warehouses.features;
      else if (Array.isArray(response.data?.warehouses)) list = response.data.warehouses;
      else if (Array.isArray(response.data)) list = response.data;
      setWarehouses(list);
    } catch {
      if (controller.signal.aborted) return;
      if (seq !== warehousesFetchSeqRef.current) return;
      setWarehouses([]);
    } finally {
      if (seq === warehousesFetchSeqRef.current) setWarehousesLoading(false);
    }
  }, [authToken, mapBounds, currentZoom]);

  const fetchTruckStops = useCallback(async () => {
    setTruckStopsLoading(true);
    try {
      const [intermodalRes, serviceRes] = await Promise.all([
        axios.get(LAYER_API.getIntermodalTerminals, {
          headers: authToken ? { Authorization: `Token ${authToken}` } : {},
        }),
        axios.get(LAYER_API.getServiceCenters, {
          headers: authToken ? { Authorization: `Token ${authToken}` } : {},
        }),
      ]);
      const intermodal = (intermodalRes.data || []).map((t) => ({ ...t, type: 'intermodal_terminal' }));
      const service = (serviceRes.data || []).map((t) => ({ ...t, type: 'service_center' }));
      setTruckStops([...intermodal, ...service]);
    } catch {
      setTruckStops([]);
    } finally {
      setTruckStopsLoading(false);
    }
  }, [authToken]);

  const fetchSubstations = useCallback(async () => {
    setSubstationsLoading(true);
    try {
      const response = await axios.get(LAYER_API.getPowerSubstations, {
        headers: authToken ? { Authorization: `Token ${authToken}` } : {},
      });
      setSubstations(Array.isArray(response.data) ? response.data : []);
    } catch {
      setSubstations([]);
    } finally {
      setSubstationsLoading(false);
    }
  }, [authToken]);

  const fetchTraffic = useCallback(async () => {
    setTrafficLoading(true);
    try {
      const response = await axios.get(LAYER_API.getTrafficData, {
        headers: authToken ? { Authorization: `Token ${authToken}` } : {},
      });
      const data = Array.isArray(response.data)
        ? response.data.map((p) => ({
            ...p,
            station_type: (p.station_type || 'ML').toString().trim().toUpperCase(),
            truck_volume: p.truck_volume ?? p.volume ?? null,
            vehicle_volume: p.vehicle_volume ?? null,
          }))
        : [];
      setTrafficData(data);
    } catch {
      setTrafficData([]);
    } finally {
      setTrafficLoading(false);
    }
  }, [authToken]);

  const fetchUtilities = useCallback(async () => {
    setUtilitiesLoading(true);
    try {
      const response = await axios.get(LAYER_API.getUtilityLoadmap, {
        headers: authToken ? { Authorization: `Token ${authToken}` } : {},
      });
      const d = response.data;
      const list = Array.isArray(d) ? d : (d?.features ?? []);
      setUtilitiesData(list);
    } catch {
      setUtilitiesData([]);
    } finally {
      setUtilitiesLoading(false);
    }
  }, [authToken]);

  const fetchDisadvantaged = useCallback(async () => {
    setDisadvantagedLoading(true);
    try {
      const response = await axios.get(LAYER_API.getDisadvantagedCommunitiesConsolidated, {
        headers: authToken ? { Authorization: `Token ${authToken}` } : {},
      });
      const d = response.data;
      let list = [];
      if (d?.type === 'FeatureCollection' && Array.isArray(d?.features)) list = d.features;
      else if (d?.disadvantaged_communities?.features) list = d.disadvantaged_communities.features;
      else if (Array.isArray(d?.disadvantaged_communities)) list = d.disadvantaged_communities;
      else if (Array.isArray(d)) list = d;
      setDisadvantagedCommunities(list);
    } catch {
      setDisadvantagedCommunities([]);
    } finally {
      setDisadvantagedLoading(false);
    }
  }, [authToken]);

  const fetchSra = useCallback(async () => {
    setSraLoading(true);
    try {
      const response = await axios.get(LAYER_API.getSCAQMDSourceReceptorAreas, {
        headers: authToken ? { Authorization: `Token ${authToken}` } : {},
      });
      const d = response.data;
      const list = Array.isArray(d) ? d : (d?.features ?? []);
      setSraData(list);
    } catch {
      setSraData([]);
    } finally {
      setSraLoading(false);
    }
  }, [authToken]);

  const fetchCounties = useCallback(async () => {
    setCountiesLoading(true);
    try {
      const response = await axios.get(LAYER_API.getCounties, {
        headers: authToken ? { Authorization: `Token ${authToken}` } : {},
      });
      const d = response.data;
      let list = [];
      if (d?.type === 'FeatureCollection' && Array.isArray(d?.features)) list = d.features;
      else if (Array.isArray(d?.counties)) list = d.counties;
      else if (Array.isArray(d)) list = d;
      setCountiesData(list);
    } catch {
      setCountiesData([]);
    } finally {
      setCountiesLoading(false);
    }
  }, [authToken]);

  const fetchCalEnviroScreen = useCallback(async () => {
    setCalEnviroScreenLoading(true);
    const percentile = Number(calEnviroScreenMinPercentile);
    const normalizedPercentile = Number.isFinite(percentile)
      ? Math.max(0, Math.min(100, percentile))
      : 75;
    try {
      const response = await axios.get(API_ENDPOINTS.regulatorCalEnviroScreen40, {
        headers: authToken ? { Authorization: `Token ${authToken}` } : {},
        params: { min_ciscorep: normalizedPercentile },
      });
      let features = [];
      if (response.data?.type === 'FeatureCollection' && Array.isArray(response.data?.features)) {
        features = response.data.features;
      } else if (Array.isArray(response.data)) {
        features = response.data;
      }
      setCalEnviroScreen(features);
    } catch {
      setCalEnviroScreen([]);
    } finally {
      setCalEnviroScreenLoading(false);
    }
  }, [authToken, calEnviroScreenMinPercentile]);

  // Lazy-load when layer is first turned on
  useEffect(() => {
    if (!existingChargersVisible) return;
    if (mhdInfrastructure.length === 0 && !mhdLoading) fetchMhd();
  }, [existingChargersVisible, mhdInfrastructure.length, mhdLoading, fetchMhd]);

  useEffect(() => {
    if (!warehousesVisible || !mapBounds) return;
    fetchWarehouses();
  }, [warehousesVisible, mapBounds, currentZoom, fetchWarehouses]);

  useEffect(() => {
    if (!intermodalTerminalsVisible && !serviceCentersVisible) return;
    if (truckStops.length === 0 && !truckStopsLoading) fetchTruckStops();
  }, [intermodalTerminalsVisible, serviceCentersVisible, truckStops.length, truckStopsLoading, fetchTruckStops]);

  useEffect(() => {
    if (!truckHotspotsVisible) return;
    // Truck hotspots use MVT in overlay; no fetch needed
  }, [truckHotspotsVisible]);

  useEffect(() => {
    if (!substationsVisible) return;
    if (substations.length === 0 && !substationsLoading) fetchSubstations();
  }, [substationsVisible, substations.length, substationsLoading, fetchSubstations]);

  useEffect(() => {
    if (!trafficVolumeVisible) return;
    if (trafficData.length === 0 && !trafficLoading) fetchTraffic();
  }, [trafficVolumeVisible, trafficData.length, trafficLoading, fetchTraffic]);

  useEffect(() => {
    if (!utilitiesVisible) return;
    if (utilitiesData.length === 0 && !utilitiesLoading) fetchUtilities();
  }, [utilitiesVisible, utilitiesData.length, utilitiesLoading, fetchUtilities]);

  useEffect(() => {
    if (!sraVisible) return;
    if (sraData.length === 0 && !sraLoading) fetchSra();
  }, [sraVisible, sraData.length, sraLoading, fetchSra]);

  useEffect(() => {
    if (!countiesVisible) return;
    if (countiesData.length === 0 && !countiesLoading) fetchCounties();
  }, [countiesVisible, countiesData.length, countiesLoading, fetchCounties]);

  useEffect(() => {
    if (!overburdenedVisible) return;
    if (disadvantagedCommunities.length === 0 && !disadvantagedLoading) fetchDisadvantaged();
  }, [overburdenedVisible, disadvantagedCommunities.length, disadvantagedLoading, fetchDisadvantaged]);

  useEffect(() => {
    if (!calEnviroScreenVisible) return;
    fetchCalEnviroScreen();
  }, [calEnviroScreenVisible, calEnviroScreenMinPercentile, fetchCalEnviroScreen]);

  // Map click for address pick
  useEffect(() => {
    if (!map || !awaitingMapPick || isProcessing) return;
    const handleMapClick = (e) => {
      if (addLocationsBlockedRef.current) return;
      const latLng = e.latLng;
      if (!latLng) return;
      const lat = typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat;
      const lng = typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng;
      setAwaitingMapPick(false);
      setAddAddressError(null);
      (async () => {
        try {
          const geocoder = geocoderRef.current;
          const result = geocoder ? await reverseGeocode(geocoder, lat, lng) : null;
          const address = result?.formatted_address || 'Selected location';
          const resolvedLat = result?.lat ?? lat;
          const resolvedLng = result?.lng ?? lng;
          await addLocation({
            site_name: '',
            raw_address: address,
            normalized_address: address,
            latitude: resolvedLat,
            longitude: resolvedLng,
          });
        } catch {
          setAddAddressError('Could not add location.');
          await addLocation({
            site_name: '',
            raw_address: 'Selected location',
            normalized_address: 'Selected location',
            latitude: lat,
            longitude: lng,
          });
        }
      })();
    };
    const listener = map.addListener?.('click', handleMapClick);
    return () => {
      if (listener && typeof listener.remove === 'function') listener.remove();
    };
  }, [map, awaitingMapPick, addLocation, isProcessing]);

  const handlePickOnMap = useCallback(() => {
    // Always allow cancelling a pending pick.
    if (awaitingMapPick) {
      setAwaitingMapPick(false);
      setAddAddressError(null);
      return;
    }
    if (addLocationsBlockedRef.current) {
      setAddAddressError('Processing is in progress. Please wait until it finishes before adding new locations.');
      return;
    }
    setAwaitingMapPick(true);
    setAddAddressError(null);
  }, [awaitingMapPick]);

  const handleOpenLocationMetrics = useCallback((loc) => {
    if (!loc || loc.latitude == null || loc.longitude == null) return;
    setSelectedMapLocation(loc);
    setMapLocationDetailsExpanded(true);
    if (map?.panTo) {
      map.panTo({ lat: loc.latitude, lng: loc.longitude });
    }
  }, [map]);

  const handleAddFromTooltip = useCallback(async () => {
    if (!addLocationTooltip) return;
    if (isProcessing) {
      setAddAddressError('Processing is in progress. Please wait until it finishes before adding new locations.');
      return;
    }
    setAddAddressError(null);
    setAddAddressLoading(true);
    try {
      await addLocation({
        site_name: (addLocationTooltip.siteName || '').trim(),
        raw_address: addLocationTooltip.rawAddress || addLocationTooltip.normalizedAddress,
        normalized_address: addLocationTooltip.normalizedAddress,
        latitude: addLocationTooltip.position.lat,
        longitude: addLocationTooltip.position.lng,
      });
      setAddLocationTooltip(null);
    } catch (err) {
      setAddAddressError(err?.message || 'Failed to add location.');
    } finally {
      setAddAddressLoading(false);
    }
  }, [addLocationTooltip, addLocation]);

  const handleUploadClick = () => {
    setUploadError(null);
    if (addLocationsBlockedRef.current) {
      setUploadError('Processing is in progress. Please wait until it finishes before uploading more locations.');
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = useCallback(async (e) => {
    if (addLocationsBlockedRef.current) {
      setUploadError('Processing is in progress. Please wait until it finishes before uploading more locations.');
      e.target.value = '';
      return;
    }
    if (uploadInFlightRef.current) {
      e.target.value = '';
      return;
    }
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const name = (file.name || '').toLowerCase();
    if (!name.endsWith('.csv') && !name.endsWith('.xlsx') && !name.endsWith('.xls')) {
      setUploadError('Please choose a CSV or Excel (.xlsx, .xls) file.');
      return;
    }
    uploadInFlightRef.current = true;
    setUploadLoading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(API_ENDPOINTS.regulatorLocationsUpload, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(authToken ? { Authorization: `Token ${authToken}` } : {}),
        },
      });
      fetchLocations();
      fetchAllLocationsForMap();
      if (data.processing_started) {
        setRunStatus({
          status: 'running',
          run_total: data.run_total ?? data.added ?? 0,
          run_processed: 0,
          total_count: data.total_count ?? 0,
          processed_count: 0,
        });
        startStatusPolling();
      }
    } catch (err) {
      setUploadError(err.response?.data?.error || err.message || 'Upload failed.');
    } finally {
      uploadInFlightRef.current = false;
      setUploadLoading(false);
    }
  }, [authToken, startStatusPolling, fetchLocations, fetchAllLocationsForMap]);

  useEffect(() => {
    if (currentUser !== undefined && !hasProductAccess(currentUser, 'regulator')) {
      navigate('/home', { replace: true });
    }
  }, [currentUser, navigate]);

  if (currentUser !== undefined && !hasProductAccess(currentUser, 'regulator')) {
    return null;
  }

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-screen text-red-600">
        Error loading Google Maps: {loadError.message}
      </div>
    );
  }

  const selectedLocationsCount = selectedLocationIds.length;
  const locationsSummaryLabel = `${locationsTotal} location${locationsTotal === 1 ? '' : 's'}`;
  const selectionSummaryLabel = `${locationsSummaryLabel} • ${selectedLocationsCount} selected`;
  const locationsSortLabel = locationsOrderBy === 'site_name'
    ? `Site name (${locationsOrderDir === 'asc' ? 'A-Z' : 'Z-A'})`
    : locationsOrderBy === 'address'
      ? `Address (${locationsOrderDir === 'asc' ? 'A-Z' : 'Z-A'})`
      : locationsOrderDir === 'desc'
        ? 'Newest'
        : 'Oldest';

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="transition-all duration-300">
        <div className="h-[100dvh] flex flex-col pb-4">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-800 font-ui">PolicyPlan - Map View</h1>
                <p className="text-sm text-gray-600">
                  Review applicant locations and supplemental data layers.
                </p>
              </div>
            </div>
          </div>

          {/* Toolbar: Add Locations (left) + Data Layers (right) */}
          {isLoaded && (
            <div className="flex items-center gap-3 px-4 py-2 bg-white border-b border-gray-200 flex-shrink-0 flex-wrap">
              {/* ADD LOCATIONS | Search Bar | Location Icon | Batch Upload | Tooltip (template) */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Add Locations</span>
                <span className="text-gray-300" aria-hidden>|</span>
                <input
                  ref={autocompleteInputRef}
                  type="text"
                  placeholder="Search address"
                  disabled={addLocationsBlocked}
                  className="w-60 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  aria-label="Search address to add (with suggestions)"
                  title="Search with Google suggestions; then add from the map tooltip."
                />
                <button
                  type="button"
                  onClick={handlePickOnMap}
                  disabled={addLocationsBlocked && !awaitingMapPick}
                  title={awaitingMapPick ? 'Cancel picking' : 'Pick a point on the map to add'}
                  className={`flex items-center justify-center w-9 h-9 rounded-md border disabled:opacity-50 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                    awaitingMapPick
                      ? 'bg-amber-100 border-amber-400 text-amber-800 hover:bg-amber-200'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                  aria-label={awaitingMapPick ? 'Cancel pick on map' : 'Pick on map'}
                >
                  <FaMapMarkerAlt className="w-4 h-4" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <div className="relative flex items-start gap-1" ref={templateInfoRef}>
                  <button
                    type="button"
                    onClick={handleUploadClick}
                    disabled={addLocationsBlocked}
                    className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Upload Excel (or CSV) with an 'address' column to add many locations at once"
                  >
                    <FaUpload className="flex-shrink-0 w-3.5 h-3.5" />
                    {uploadLoading ? 'Uploading…' : 'Batch Upload'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setTemplateInfoOpen((o) => !o)}
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full text-gray-400 hover:text-blue-600 hover:bg-blue-50 flex-shrink-0 mt-0.5"
                    aria-label="Batch upload template info"
                    title="Template and download"
                  >
                    <FaInfoCircle className="w-3 h-3" aria-hidden />
                  </button>
                  {templateInfoOpen && (
                    <div className="absolute left-0 top-full mt-1 z-50 min-w-[240px] max-w-[280px] p-3 bg-white border border-gray-200 rounded-md shadow-lg">
                      <p className="text-xs text-gray-600 mb-3">
                        Use an Excel file with an <strong>&quot;address&quot;</strong> column and an optional <strong>&quot;site_name&quot;</strong> column to add many locations at once. Each address cell can be a street address or geo-coordinates (<strong>lat,lng</strong>, e.g. 34.0522,-118.2437). Optionally add <strong>&quot;Expected Capacity (MW)&quot;</strong> and <strong>&quot;Expected Year&quot;</strong> columns to assess whether the capacity outlook supports your deployment. Download the template (Greater LA examples) below.
                      </p>
                      <button
                        type="button"
                        onClick={() => { downloadTemplate(); setTemplateInfoOpen(false); }}
                        className="flex items-center gap-1.5 px-2 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded hover:bg-gray-50"
                        title="Download Excel template with 'address' column"
                      >
                        <FaDownload className="w-3 h-3" />
                        Download Excel Template
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsDataLayersOpen(true)}
                className="ml-auto flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors shadow-sm"
                aria-label="Open Data Layers"
              >
                <FiLayers className="w-4 h-4 text-gray-500" />
                <span>Data Layers</span>
                {activeDataLayerCount > 0 && (
                  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full font-medium">
                    {activeDataLayerCount}
                  </span>
                )}
              </button>
            </div>
          )}

          <div className="flex-1 flex min-h-0">
            <div className={`flex-1 min-w-0 relative ${awaitingMapPick ? 'cursor-crosshair' : ''}`}>
              {!isLoaded ? (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-600">
                  Loading map...
                </div>
              ) : (
                <>
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={defaultCenter}
                    zoom={10}
                    onLoad={onMapLoad}
                    onUnmount={onMapUnmount}
                    onClick={handleMapBackgroundClick}
                    options={{
                      mapId: 'DEMO_MAP_ID', // Required for interleaved deck.gl rendering
                      clickableIcons: false,
                      mapTypeControl: true,
                      streetViewControl: false,
                      fullscreenControl: true,
                      gestureHandling: 'greedy',
                    }}
                  >
                    {applicantAddressesForMap.map((point) => (
                      <Marker
                        key={`addr-${point.id}`}
                        position={{ lat: point.lat, lng: point.lng }}
                        title={point.address}
                        icon={
                          selectedLocationIds.includes(point.id) || selectedMapLocation?.id === point.id
                            ? applicantMarkerIcons.red
                            : applicantMarkerIcons.blue
                        }
                        onClick={() => {
                          setSelectedMapLocation(point.location);
                          setMapLocationDetailsExpanded(true);
                        }}
                      />
                    ))}
                    {addLocationTooltip && (
                      <InfoWindow
                        position={addLocationTooltip.position}
                        options={{ maxWidth: 340 }}
                        onCloseClick={() => setAddLocationTooltip(null)}
                      >
                        <div className="p-1 min-w-[320px]">
                          <p className="text-sm font-medium text-gray-800 mb-1">Searched address</p>
                          <input
                            type="text"
                            value={addLocationTooltip.siteName || ''}
                            onChange={(e) =>
                              setAddLocationTooltip((prev) => (prev ? { ...prev, siteName: e.target.value } : prev))
                            }
                            placeholder="Site name (optional)"
                            className="block w-[calc(100%-0.5rem)] max-w-full box-border px-2 py-1.5 border border-gray-300 rounded text-xs mb-2 focus:ring-2 focus:ring-blue-500"
                            aria-label="Site name for this location"
                          />
                          <p className="text-xs text-gray-600 mb-3 line-clamp-2">{addLocationTooltip.normalizedAddress}</p>
                          <button
                            type="button"
                            onClick={handleAddFromTooltip}
                            disabled={addLocationsBlocked}
                            className="w-full px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            {addAddressLoading ? 'Adding…' : 'Add to list'}
                          </button>
                        </div>
                      </InfoWindow>
                    )}
                    {circuitTooltip && (
                      <InfoWindow
                        position={circuitTooltip.position}
                        options={{ maxWidth: 300 }}
                        onCloseClick={() => setCircuitTooltip(null)}
                      >
                        <div className="p-2.5 min-w-[260px]" style={{ maxWidth: 300 }}>
                          <h3 className="text-base font-bold mb-2" style={{ color: '#3B82F6' }}>
                            Circuit Information
                          </h3>
                          <p className="text-xs text-gray-700 mb-1">
                            <strong>Utility:</strong> {getCircuitProp(circuitTooltip.circuit?.properties, 'utility', 'Utility') ?? 'N/A'}
                          </p>
                          <p className="text-xs text-gray-700 mb-1">
                            <strong>Circuit Name:</strong> {getCircuitProp(circuitTooltip.circuit?.properties, 'circuit_name', 'Circuit_Name', 'name') ?? 'N/A'}
                          </p>
                          {/* <p className="text-xs text-gray-700 mb-1">
                            <strong>Circuit ID:</strong> {getCircuitProp(circuitTooltip.circuit?.properties, 'circuit_id', 'Circuit_ID', 'id') ?? 'N/A'}
                          </p> */}
                          <p className="text-xs text-gray-700 mb-1">
                            <strong>Voltage (kV):</strong> {getCircuitProp(circuitTooltip.circuit?.properties, 'circuit_voltage', 'voltage', 'voltage_kv', 'kv') ?? 'N/A'}
                          </p>
                          <p className="text-xs text-gray-700 mb-1">
                            <strong>Hosting Capacity (MW):</strong> {formatHostingCapacityMw(circuitTooltip.circuit?.properties)}
                          </p>
                          <p className="text-xs text-gray-700 mb-1">
                            <strong>Substation Name:</strong> {splitSubstationNameBank(getCircuitProp(circuitTooltip.circuit?.properties, 'substation_name', 'substation')).name}
                          </p>
                          <p className="text-xs text-gray-700 mb-0">
                            <strong>Bank:</strong> {splitSubstationNameBank(getCircuitProp(circuitTooltip.circuit?.properties, 'substation_name', 'substation')).bank}
                          </p>
                          {(() => {
                            const rawAlc = getCircuitProp(circuitTooltip.circuit?.properties, 'alc_multiyear');
                            const circuitAlc = circuitAlcDisplay(rawAlc);
                            const aBank = circuitABankDisplay(rawAlc);
                            return (
                              <>
                                {circuitAlc.outlookEntries.length > 0 && (
                                  <div className="mt-1.5 pl-2 border-l-2 border-blue-300">
                                    <p className="text-xs font-semibold text-gray-800 mb-0.5">
                                      Est. Capacity Outlook
                                    </p>
                                    <p className="text-[11px] text-gray-600 mb-0">
                                      {formatOutlookEntriesLine(circuitAlc.outlookEntries)}
                                    </p>
                                  </div>
                                )}
                                <p className="text-xs text-gray-700 mb-1 mt-1 inline-flex items-center gap-1">
                                  <span>
                                    <strong>A Bank:</strong> {aBank.name ?? 'N/A'}
                                  </span>
                                  <ABankInfoTip />
                                </p>
                                <p className="text-xs text-gray-700 mb-0">
                                  <strong>A Bank Capacity (MW):</strong> {aBank.currentMw ?? 'N/A'}
                                </p>
                                {aBank.outlookEntries.length > 0 && (
                                  <div className="mt-1.5 pl-2 border-l-2 border-amber-300">
                                    <p className="text-xs font-semibold text-gray-800 mb-0.5">
                                      A Bank Outlook
                                    </p>
                                    <p className="text-[11px] text-gray-600 mb-0">
                                      {formatOutlookEntriesLine(aBank.outlookEntries)}
                                    </p>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </InfoWindow>
                    )}
                    {substationTooltip && (
                      <InfoWindow
                        position={substationTooltip.position}
                        options={{ maxWidth: 320 }}
                        onCloseClick={() => { setSubstationTooltip(null); setSelectedSubstation(null); }}
                      >
                        <div className="p-2.5 min-w-[260px]" style={{ maxWidth: 320 }}>
                          <h3 className="text-base font-bold mb-2" style={{ color: '#B45309' }}>
                            Substation Information
                          </h3>
                          {(() => {
                            const banks = Array.isArray(substationTooltip.substation?.banks)
                              ? substationTooltip.substation.banks
                              : [substationTooltip.substation].filter(Boolean);
                            const site = splitSubstationNameBank(banks[0]?.substation_name).name;
                            const systems = [...new Set(banks.map((b) => b?.system_name).filter(Boolean))];
                            return (
                              <>
                                <p className="text-xs text-gray-700 mb-1">
                                  <strong>Substation:</strong> {site}
                                </p>
                                <p className="text-xs text-gray-700 mb-1">
                                  <strong>System:</strong> {systems.length ? systems.join(', ') : 'N/A'}
                                </p>
                                <p className="text-xs text-gray-700 mb-1">
                                  <strong>{banks.length > 1 ? `${banks.length} transformer banks:` : 'Bank:'}</strong>
                                </p>
                                {banks.map((b, idx) => {
                                  const entries = substationBankOutlookEntries(b);
                                  const fallback = formatSubstationMwValue(b?.capacity_mw) ?? 'N/A';
                                  return (
                                    <div key={`${b?.substation_id ?? idx}`} className="mb-1.5 pl-2 border-l-2 border-amber-300">
                                      <p className="text-xs font-semibold text-gray-800 mb-0.5">
                                        Bank {splitSubstationNameBank(b?.substation_name).bank}
                                      </p>
                                      <p className="text-[11px] text-gray-600 mb-0">
                                        Est. Available:{' '}
                                        {entries.length > 0
                                          ? entries.map((e) => `${e.year}: ${e.mw}`).join('  |  ')
                                          : fallback}
                                      </p>
                                    </div>
                                  );
                                })}
                              </>
                            );
                          })()}
                        </div>
                      </InfoWindow>
                    )}
                    {chargingStationTooltip && (
                      <InfoWindow
                        position={chargingStationTooltip.position}
                        options={{ maxWidth: 300 }}
                        onCloseClick={() => setChargingStationTooltip(null)}
                      >
                        <div className="p-2.5 min-w-[260px]" style={{ maxWidth: 300 }}>
                          <h3 className="text-base font-bold mb-2" style={{ color: '#059669' }}>
                            Charging Station Information
                          </h3>
                          {[
                            { key: 'name', label: 'Name' },
                            { key: 'addr', label: 'Address' },
                            { key: 'status', label: 'Status' },
                            { key: 'type_of_access', label: 'Access Type' },
                            { key: 'stall_count', label: 'Stall Count' },
                            { key: 'plug_count', label: 'Plug Count' },
                            { key: 'max_power', label: 'Max Power (kW)' },
                            { key: 'facility_type', label: 'Facility Type' },
                          ].map(({ key, label }) => {
                            const raw = chargingStationTooltip.station[key];
                            const isStallCount = key === 'stall_count';
                            const display = isStallCount && (raw == null || raw === '' || Number(raw) === 0)
                              ? '—'
                              : (raw ?? 'N/A');
                            return (
                              <p key={key} className="text-xs text-gray-700 mb-1">
                                <strong>{label}:</strong> {display}
                              </p>
                            );
                          })}
                        </div>
                      </InfoWindow>
                    )}
                    {warehouseTooltip && (
                      <InfoWindow
                        position={warehouseTooltip.position}
                        options={{ maxWidth: 300 }}
                        onCloseClick={() => setWarehouseTooltip(null)}
                      >
                        <div className="p-2.5 min-w-[260px]" style={{ maxWidth: 300 }}>
                          <h3 className="text-base font-bold mb-2" style={{ color: '#059669' }}>
                            Warehouse Information
                          </h3>
                          {[
                            { label: 'Name', value: getWarehouseProp(warehouseTooltip.warehouse, 'place_name', 'name') },
                            { label: 'APN', value: getWarehouseProp(warehouseTooltip.warehouse, 'apn', 'APN') },
                            { label: 'Area (sqft)', value: formatSquareFootage(getWarehouseProp(warehouseTooltip.warehouse, 'shape_area', 'area', 'Area')) },
                            { label: 'Category', value: getWarehouseProp(warehouseTooltip.warehouse, 'category') },
                            { label: 'Warehouse Type', value: getWarehouseProp(warehouseTooltip.warehouse, 'warehouse_type', 'warehouse_class') },
                            { label: 'County', value: getWarehouseProp(warehouseTooltip.warehouse, 'county', 'County') },
                          ].map(({ label, value }) => (
                            <p key={label} className="text-xs text-gray-700 mb-1">
                              <strong>{label}:</strong> {value ?? 'N/A'}
                            </p>
                          ))}
                        </div>
                      </InfoWindow>
                    )}
                    {truckStopTooltip && (
                      <InfoWindow
                        position={truckStopTooltip.position}
                        options={{ maxWidth: 300 }}
                        onCloseClick={() => setTruckStopTooltip(null)}
                      >
                        <div className="p-2.5 min-w-[260px]" style={{ maxWidth: 300 }}>
                          <h3 className="text-base font-bold mb-2" style={{ color: '#059669' }}>
                            {getTruckStopTooltipTitle(truckStopTooltip.truckStop?.type)}
                          </h3>
                          {[
                            { label: 'Name', value: truckStopTooltip.truckStop?.name },
                            { label: 'Type', value: formatTruckStopType(truckStopTooltip.truckStop?.type) },
                            { label: 'Street Address', value: truckStopTooltip.truckStop?.street },
                            { label: 'City', value: truckStopTooltip.truckStop?.city },
                            { label: 'State', value: truckStopTooltip.truckStop?.state },
                            { label: 'ZIP Code', value: truckStopTooltip.truckStop?.zip },
                            { label: 'County', value: truckStopTooltip.truckStop?.county },
                          ].map(({ label, value }) => (
                            <p key={label} className="text-xs text-gray-700 mb-1">
                              <strong>{label}:</strong> {value ?? 'N/A'}
                            </p>
                          ))}
                        </div>
                      </InfoWindow>
                    )}
                    {sraTooltip && (
                      <InfoWindow
                        position={sraTooltip.position}
                        options={{ maxWidth: 240 }}
                        onCloseClick={() => setSraTooltip(null)}
                      >
                        <div className="p-2.5 min-w-[200px]" style={{ maxWidth: 240 }}>
                          <h3 className="text-base font-bold mb-2" style={{ color: '#0e7490' }}>
                            SRA Information
                          </h3>
                          <p className="text-xs text-gray-700 mb-0">
                            <strong>SRA (MONITORING):</strong> {sraTooltip.sra?.properties?.monitoring ?? sraTooltip.sra?.monitoring ?? 'N/A'}
                          </p>
                        </div>
                      </InfoWindow>
                    )}
                  </GoogleMap>
                  {selectedMapLocation && mapLocationDetailsExpanded && (
                    <div className="absolute bottom-6 right-6 z-10 w-[462px] max-w-[calc(100%-3rem)] max-h-[calc(100%-3rem)] overflow-hidden rounded-xl border border-gray-200 bg-white/95 shadow-xl backdrop-blur-sm">
                      <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-800 line-clamp-2">
                            {getLocationPrimaryLabel(selectedMapLocation)}
                          </p>
                          {getLocationSecondaryLabel(selectedMapLocation) && (
                            <p className="mt-1 text-xs text-gray-600 line-clamp-2">
                              {getLocationSecondaryLabel(selectedMapLocation)}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedMapLocation(null);
                            setMapLocationDetailsExpanded(false);
                          }}
                          className="shrink-0 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                          aria-label="Close location details"
                        >
                          ×
                        </button>
                      </div>
                      <div className="max-h-[calc(100vh-16rem)] overflow-y-auto px-4 py-3">
                        <CapacityAssessmentEditor
                          location={selectedMapLocation}
                          onSave={(payload) => saveExpectedDeployment(selectedMapLocation.id, payload)}
                        />
                        <LocationMetricsDetails
                          metrics={selectedMapLocation.metrics}
                          selectedCircuit={selectedCircuit}
                          onCircuitToggle={handleCircuitToggleFromLocation}
                        />
                      </div>
                    </div>
                  )}
                  {map && (
                    <MapScaleBar
                      zoom={currentZoom}
                      centerLat={
                        mapBounds
                          ? (mapBounds.north + mapBounds.south) / 2
                          : defaultCenter.lat
                      }
                      imperial={scaleImperial}
                      onUnitToggle={() => setScaleImperial((prev) => !prev)}
                    />
                  )}
                  {map && (
                    <RegulatorChargerLayersOverlay
                      map={map}
                      currentZoom={currentZoom}
                      parcelsVisible={false}
                      circuitsVisible={powerGridCapacityVisible}
                      substationsVisible={substationsVisible}
                      heatmapVisible={truckHotspotsVisible}
                      contoursVisible={powerGridHeatmapVisible}
                      selectedCircuit={selectedCircuit}
                      selectedSubstation={selectedSubstation}
                      onCircuitClick={handleCircuitClick}
                      onSubstationClick={handleSubstationClick}
                      onChargingStationClick={handleChargingStationClick}
                      onWarehouseClick={handleWarehouseClick}
                      onTruckStopClick={handleTruckStopClick}
                      onSraClick={handleSraClick}
                      existingChargersVisible={existingChargersVisible}
                      warehousesVisible={warehousesVisible}
                      intermodalTerminalsVisible={intermodalTerminalsVisible}
                      serviceCentersVisible={serviceCentersVisible}
                      trafficVolumeVisible={trafficVolumeVisible}
                      utilitiesVisible={utilitiesVisible}
                      sraVisible={sraVisible}
                      countiesVisible={countiesVisible}
                      overburdenedVisible={overburdenedVisible}
                      calEnviroScreenVisible={calEnviroScreenVisible}
                      mhdInfrastructure={mhdInfrastructure}
                      substations={substations}
                      warehouses={warehouses}
                      truckStops={truckStops}
                      trafficData={trafficData}
                      utilitiesData={utilitiesData}
                      sraData={sraData}
                      countiesData={countiesData}
                      disadvantagedCommunities={disadvantagedCommunities}
                      calEnviroScreen={calEnviroScreen}
                      interactiveTopLayerId={
                        effectiveInteractiveTopLayerId === AUTO_CLICK_PRIORITY_LAYER_ID
                          ? null
                          : effectiveInteractiveTopLayerId
                      }
                    />
                  )}
                </>
              )}
            </div>

            {/* Right panel: Applicant Locations (collapsible) */}
            {addressListCollapsed ? (
              <div className="w-12 flex-shrink-0 bg-white border-l border-gray-200 flex flex-col items-center py-4">
                <button
                  type="button"
                  onClick={toggleAddressListCollapse}
                  className="flex flex-col items-center gap-2 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                  aria-label="Expand address list"
                  title="Expand address list"
                >
                  <FaChevronLeft className="w-4 h-4" />
                  <span className="text-[10px] font-medium uppercase tracking-wide writing-mode-vertical transform rotate-180 select-none" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
                    Locations
                  </span>
                </button>
              </div>
            ) : (
            <div className="w-96 max-w-lg bg-white border-l border-gray-200 overflow-y-auto flex-shrink-0 flex flex-col transition-all duration-300">
              <div className="p-4 border-b border-gray-100 flex-shrink-0">
                <div className="h-10 flex items-center justify-between gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-800 truncate min-w-0 flex-1">Applicant Locations</h3>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {selectedLocationIds.length > 0 && (
                      <>
                        <button
                          type="button"
                          onClick={handleUpdateSelectedMetrics}
                          disabled={updateMetricsLoading || runStatus?.status === 'running'}
                          className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap shrink-0 bg-blue-600 text-white hover:bg-blue-700 transition-opacity ${
                            updateMetricsLoading || runStatus?.status === 'running' ? 'opacity-50' : ''
                          }`}
                        >
                          {updateMetricsLoading ? 'Updating…' : `Update metrics (${selectedLocationIds.length})`}
                        </button>
                        <button
                          type="button"
                          onClick={openDeleteConfirm}
                          disabled={deleteLocationsLoading}
                          className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap shrink-0 bg-red-600 text-white hover:bg-red-700 transition-opacity ${
                            deleteLocationsLoading ? 'opacity-50' : ''
                          }`}
                        >
                          <FaTrash />
                          Delete ({selectedLocationIds.length})
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={toggleAddressListCollapse}
                      className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                      aria-label="Collapse address list"
                      title="Collapse address list"
                    >
                      <FaChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {runStatus?.status === 'running' && (
                  <>
                    <div className="mb-2 flex items-center justify-between gap-2 p-2 bg-blue-50 rounded text-sm text-blue-800">
                      <div className="min-w-0">
                        Processing…{' '}
                        {runStatus.run_total != null
                          ? `${runStatus.run_processed ?? 0} / ${runStatus.run_total}`
                          : `${runStatus.processed_count ?? 0} / ${runStatus.total_count ?? 0}`}
                      </div>
                      <button
                        type="button"
                        onClick={handleCancelRun}
                        disabled={cancelRunLoading}
                        className="shrink-0 rounded-md border border-red-200 bg-white px-2.5 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        Cancel run
                      </button>
                    </div>
                    <div className="mb-3 h-1.5 bg-gray-100 overflow-hidden rounded-full" role="progressbar" aria-valuenow={metricsProgressPct} aria-valuemin={0} aria-valuemax={100} aria-label="Computing metrics">
                      <div
                        className="h-full bg-blue-500 transition-all duration-300 rounded-full"
                        style={{ width: `${metricsProgressPct}%` }}
                      />
                    </div>
                  </>
                )}
                {runStatus?.status !== 'running' && (
                  <CircuitUpdateBanner
                    count={circuitStaleness.count}
                    noun="location"
                    onRefresh={handleRefreshStaleCircuits}
                  />
                )}
                {awaitingMapPick && (
                  <p className="text-sm text-amber-700 bg-amber-50 px-2 py-1.5 rounded mt-2">
                    Click on the map to add a location.
                  </p>
                )}
                {addAddressError && <p className="text-sm text-red-600 mt-2">{addAddressError}</p>}
                {uploadError && <p className="text-sm text-red-600 mt-2">{uploadError}</p>}
              </div>

              <div className="flex-1 flex flex-col min-h-0 px-4 pt-2 pb-4">
                <div className="mb-2">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Filter list..."
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                    aria-label="Filter locations in list"
                  />
                </div>
                {locations.length > 0 && (
                  <div className="mb-3 space-y-1.5">
                    <div className="flex items-center justify-between gap-3">
                      <p className="min-w-0 text-xs text-gray-600">
                        {selectionSummaryLabel}
                      </p>
                      <div className="relative shrink-0" ref={orderByDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setOrderByDropdownOpen((o) => !o)}
                          className="text-xs text-blue-600 hover:underline whitespace-nowrap"
                          aria-expanded={orderByDropdownOpen}
                          aria-haspopup="true"
                        >
                          Sort: {locationsSortLabel}
                        </button>
                        {orderByDropdownOpen && (
                          <div className="absolute right-0 top-full mt-0.5 z-20 min-w-[180px] py-1 bg-white border border-gray-200 rounded shadow-md">
                            <button
                              type="button"
                              onClick={() => { setLocationsOrderBy('site_name'); setLocationsOrderDir('asc'); setLocationsPage(1); setOrderByDropdownOpen(false); }}
                              className={`block w-full text-left px-2 py-1 text-xs hover:bg-gray-50 ${locationsOrderBy === 'site_name' && locationsOrderDir === 'asc' ? 'text-blue-600 font-medium' : 'text-blue-600'}`}
                            >
                              Site name (A→Z)
                            </button>
                            <button
                              type="button"
                              onClick={() => { setLocationsOrderBy('site_name'); setLocationsOrderDir('desc'); setLocationsPage(1); setOrderByDropdownOpen(false); }}
                              className={`block w-full text-left px-2 py-1 text-xs hover:bg-gray-50 ${locationsOrderBy === 'site_name' && locationsOrderDir === 'desc' ? 'text-blue-600 font-medium' : 'text-blue-600'}`}
                            >
                              Site name (Z→A)
                            </button>
                            <button
                              type="button"
                              onClick={() => { setLocationsOrderBy('address'); setLocationsOrderDir('asc'); setLocationsPage(1); setOrderByDropdownOpen(false); }}
                              className={`block w-full text-left px-2 py-1 text-xs hover:bg-gray-50 ${locationsOrderBy === 'address' && locationsOrderDir === 'asc' ? 'text-blue-600 font-medium' : 'text-blue-600'}`}
                            >
                              Address (A→Z)
                            </button>
                            <button
                              type="button"
                              onClick={() => { setLocationsOrderBy('address'); setLocationsOrderDir('desc'); setLocationsPage(1); setOrderByDropdownOpen(false); }}
                              className={`block w-full text-left px-2 py-1 text-xs hover:bg-gray-50 ${locationsOrderBy === 'address' && locationsOrderDir === 'desc' ? 'text-blue-600 font-medium' : 'text-blue-600'}`}
                            >
                              Address (Z→A)
                            </button>
                            <button
                              type="button"
                              onClick={() => { setLocationsOrderBy('created_at'); setLocationsOrderDir('desc'); setLocationsPage(1); setOrderByDropdownOpen(false); }}
                              className={`block w-full text-left px-2 py-1 text-xs hover:bg-gray-50 ${locationsOrderBy === 'created_at' && locationsOrderDir === 'desc' ? 'text-blue-600 font-medium' : 'text-blue-600'}`}
                            >
                              Time uploaded (newest first)
                            </button>
                            <button
                              type="button"
                              onClick={() => { setLocationsOrderBy('created_at'); setLocationsOrderDir('asc'); setLocationsPage(1); setOrderByDropdownOpen(false); }}
                              className={`block w-full text-left px-2 py-1 text-xs hover:bg-gray-50 ${locationsOrderBy === 'created_at' && locationsOrderDir === 'asc' ? 'text-blue-600 font-medium' : 'text-blue-600'}`}
                            >
                              Time uploaded (oldest first)
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={selectAllOnPage}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Select page
                      </button>
                      <button
                        type="button"
                        onClick={selectAllLocations}
                        disabled={selectAllLoading || locationsTotal === 0}
                        className={`text-xs ${selectAllLoading || locationsTotal === 0 ? 'text-blue-300 cursor-default' : 'text-blue-600 hover:underline'}`}
                      >
                        {selectAllLoading ? 'Selecting…' : 'Select all'}
                      </button>
                      <button
                        type="button"
                        onClick={clearSelection}
                        disabled={selectedLocationsCount === 0}
                        className={`text-xs ${selectedLocationsCount > 0 ? 'text-gray-600 hover:underline' : 'text-gray-400 cursor-default'}`}
                      >
                        Clear
                      </button>
                      {locationsUpdatingVisible && (
                        <span className="text-xs text-gray-400" aria-live="polite">
                          Updating…
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowAssessmentDots((prev) => !prev)}
                        aria-pressed={showAssessmentDots}
                        className="ml-auto inline-flex items-center gap-2 text-xs text-gray-600 hover:text-gray-800"
                      >
                        <span>Capacity Assessment</span>
                        <span
                          className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
                            showAssessmentDots ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                          aria-hidden="true"
                        >
                          <span
                            className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                              showAssessmentDots ? 'translate-x-3.5' : 'translate-x-0.5'
                            }`}
                          />
                        </span>
                      </button>
                    </div>
                  </div>
                )}
                {locations.length > 0 ? (
                  <>
                    <ul className={`flex-1 overflow-y-auto space-y-2 pr-1 min-h-0 ${locationsLoading ? 'opacity-70' : ''}`}>
                      {locations.map((loc) => {
                        const assessment = loc.capacity_assessment || null;
                        const assessmentMeta = getAssessmentMeta(assessment?.status);
                        const assessmentTooltip = buildAssessmentTooltip(assessment);
                        return (
                        <li
                          key={loc.id}
                          className={`text-sm p-2 rounded-md border transition flex items-start gap-2 ${
                            selectedLocationIds.includes(loc.id)
                              ? 'border-blue-500 bg-blue-50/50'
                              : 'border-gray-200 bg-gray-50/50 hover:bg-blue-50/30'
                          }`}
                          title={loc.normalized_address || loc.raw_address}
                        >
                          <input
                            type="checkbox"
                            checked={selectedLocationIds.includes(loc.id)}
                            onChange={() => toggleLocationSelection(loc.id)}
                            className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            aria-label={`Select ${loc.normalized_address || loc.raw_address}`}
                          />
                          <div className="min-w-0 flex-1 flex items-start gap-2">
                            <div className="min-w-0 flex-1">
                            <span className="text-gray-800 line-clamp-2">{getLocationPrimaryLabel(loc)}</span>
                            {getLocationSecondaryLabel(loc) && (
                              <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{getLocationSecondaryLabel(loc)}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              {loc.latitude != null && loc.longitude != null ? (
                                <>
                                  {loc.processing_status === 'completed' && loc.metrics && (
                                    <button
                                      type="button"
                                      onClick={() => handleOpenLocationMetrics(loc)}
                                      className="text-xs text-green-600 hover:text-green-700 hover:underline"
                                    >
                                      Metrics
                                    </button>
                                  )}
                                </>
                              ) : (
                                <>
                                  <span className="text-xs text-amber-600">Could not geocode</span>
                                  <span className="mx-1 text-gray-400">|</span>
                                </>
                              )}
                              {editingSiteNameId === loc.id ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="text"
                                    value={editingSiteNameValue}
                                    onChange={(e) => setEditingSiteNameValue(e.target.value)}
                                    className="w-32 px-2 py-1 border border-gray-300 rounded text-xs"
                                    placeholder="Site name"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => saveSiteName(loc.id)}
                                    disabled={savingSiteNameId === loc.id}
                                    className="text-xs text-blue-700 hover:underline disabled:opacity-50"
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => { setEditingSiteNameId(null); setEditingSiteNameValue(''); }}
                                    className="text-xs text-gray-500 hover:underline"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingSiteNameId(loc.id);
                                    setEditingSiteNameValue(loc.site_name || '');
                                  }}
                                  className="text-xs text-blue-600 hover:underline"
                                >
                                  {loc.site_name ? 'Edit site name' : 'Add site name'}
                                </button>
                              )}
                            </div>
                            </div>
                            {showAssessmentDots && (
                              <span
                                className={`mt-0.5 inline-block h-2.5 w-2.5 rounded-full border border-white/80 shadow-sm ${assessmentMeta.dotClass}`}
                                title={assessmentTooltip}
                                aria-label={assessmentTooltip}
                              />
                            )}
                          </div>
                        </li>
                        );
                      })}
                    </ul>
                    {locationsTotal > locationsPageSize && (
                      <div className="flex items-center justify-between mt-2 text-sm">
                        <button
                          type="button"
                          disabled={locationsPage <= 1}
                          onClick={() => setLocationsPage((p) => Math.max(1, p - 1))}
                          className="text-blue-600 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <span>
                          Page {locationsPage} of {Math.ceil(locationsTotal / locationsPageSize)}
                        </span>
                        <button
                          type="button"
                          disabled={locationsPage >= Math.ceil(locationsTotal / locationsPageSize)}
                          onClick={() => setLocationsPage((p) => p + 1)}
                          className="text-blue-600 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-500">
                    {locationsLoading ? 'Loading…' : 'No locations yet. Add an address, pick on map, or upload CSV/Excel.'}
                  </p>
                )}
              </div>
            </div>
            )}
          </div>
        </div>
      </main>

      <RegulatorDataLayersDrawer
        isOpen={isDataLayersOpen}
        onClose={() => setIsDataLayersOpen(false)}
        groups={dataLayerGroups}
        clickPriorityOptions={activeDataLayerOptions}
        clickPriorityValue={effectiveInteractiveTopLayerId}
        onClickPriorityChange={setInteractiveTopLayerId}
      />

      {updateCompleteOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="update-complete-title"
          onClick={() => setUpdateCompleteOpen(false)}
        >
          <div className="absolute inset-0 bg-black/30" />
          <div
            className="relative w-full max-w-sm bg-white rounded-lg shadow-xl border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5">
              <h3 id="update-complete-title" className="text-lg font-semibold text-gray-900">
                Metrics update complete
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                Metrics update finished for{' '}
                <span className="font-semibold text-gray-900">{updateCompleteCount}</span>{' '}
                location{updateCompleteCount === 1 ? '' : 's'}.
              </p>
            </div>
            <div className="px-5 py-4 bg-gray-50 rounded-b-lg flex items-center justify-end">
              <button
                type="button"
                onClick={() => setUpdateCompleteOpen(false)}
                className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-confirm-title"
          onClick={() => {
            if (!deleteLocationsLoading) setDeleteConfirmOpen(false);
          }}
        >
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="relative w-full max-w-md bg-white rounded-lg shadow-xl border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5">
              <h3 id="delete-confirm-title" className="text-lg font-semibold text-gray-900">
                Delete selected locations?
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                You&apos;re about to delete{' '}
                <span className="font-semibold text-gray-900">{selectedLocationIds.length}</span>{' '}
                location{selectedLocationIds.length === 1 ? '' : 's'}. This cannot be undone.
              </p>
              {uploadError && <p className="mt-3 text-sm text-red-600">{uploadError}</p>}
            </div>

            <div className="px-5 py-4 bg-gray-50 rounded-b-lg flex items-center justify-end gap-2">
              <button
                type="button"
                ref={deleteCancelButtonRef}
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deleteLocationsLoading}
                className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteSelected}
                disabled={deleteLocationsLoading}
                className="px-3 py-2 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteLocationsLoading ? 'Deleting…' : `Delete (${selectedLocationIds.length})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegulatorMapView;
